// Presupuestos Page - Improved with table view and filters
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { presupuestosAPI, clientesAPI, menusAPI, platosAPI, alergenosAPI, personasContactoAPI } from '../api/api';
import { Plus, X, Trash2, Check, ChevronUp, ChevronDown, Search, Calendar, Layers, MoreHorizontal, ExternalLink, SlidersHorizontal, UserPlus, ChevronRight } from 'lucide-react';
import DatePicker from '../components/DatePicker';
import DireccionAutocomplete from '../components/DireccionAutocomplete';
import AlergenoIcon from '../components/AlergenoIcon';
import { textoRangoFechas } from '../utils/multieventoCabecera';
import { toast } from '../hooks/use-toast';
import { lineaDesdeMenu, lineasVentaUnDia, precioMenuCatalogo } from '../utils/lineasVenta';
import { fechaLocalISO, normalizarEstadoPago, parsearFechaServidor, pendientesEventoConfirmado } from '../utils/pantallasUnDia';
import useEsMovil from '../hooks/useEsMovil';
import useCapaAtras from '../hooks/useCapaAtras';
import HojaMovil from '../components/movil/HojaMovil';
import FilaHojaMovil, { SeparadorHojaMovil } from '../components/movil/FilaHojaMovil';

// Estados del filtro del listado (el select del escritorio y los chips del móvil)
const ESTADOS_FILTRO = [
  { valor: 'borrador', texto: 'Borrador' },
  { valor: 'enviado', texto: 'Enviado' },
  { valor: 'aceptado', texto: 'Aceptado' },
  { valor: 'rechazado', texto: 'Rechazado' },
  { valor: 'confirmado', texto: 'Confirmado' },
  { valor: 'cancelado', texto: 'Cancelado' },
];

// Texto del estado en las tarjetas del móvil
const textoEstado = (estado) => {
  const e = ESTADOS_FILTRO.find(x => x.valor === estado);
  if (e) return e.texto;
  if (estado === 'confirmando') return 'Confirmando';
  return estado ? String(estado).charAt(0).toUpperCase() + String(estado).slice(1) : 'Borrador';
};

// Campos del móvil: 44 px de alto y 16 px (sin zoom de iOS), como HojaEditarLineaMovil
const CAMPO_MOVIL = 'w-full h-11 px-3 border border-[#CBD3CF] rounded-lg text-base text-[#16221D] bg-white focus:outline-none focus:ring-2 focus:ring-duetto-primary';
const ETIQUETA_MOVIL = 'block text-sm font-medium text-gray-700 mb-1';
const SECCION_MOVIL = 'text-xs font-bold uppercase tracking-wide text-[#8A9892] pt-2';

const DIAS_SEMANA_CORTOS = ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'];

// Función para formatear fechas a DD/MM/YYYY (U-100: un día 'YYYY-MM-DD' es
// local; una fecha con hora sin zona es UTC, como la guarda el servidor)
const formatFecha = (fechaStr) => {
  if (!fechaStr) return '';
  const fecha = parsearFechaServidor(fechaStr);
  if (!fecha) return '';
  const dia = String(fecha.getDate()).padStart(2, '0');
  const mes = String(fecha.getMonth() + 1).padStart(2, '0');
  const anio = fecha.getFullYear();
  return `${dia}/${mes}/${anio}`;
};

// Extraer solo el número de factura (sin prefijo DC2025)
const extraerNumeroFactura = (numeroCompleto) => {
  if (!numeroCompleto) return '';
  const str = String(numeroCompleto);
  // Si tiene formato DC2025XXXX, extraer solo el número
  const match = str.match(/DC\d{4}(\d+)$/);
  if (match) return match[1];
  // Si es solo número, devolverlo tal cual
  return str;
};

// Multievento en el listado (M-39). Facturas de la fila: todas las de la
// cabecera y de las jornadas (`numeros_factura` del servidor); si no vienen,
// la primera como antes
const numerosFacturaDe = (p) => {
  if (Array.isArray(p?.numeros_factura) && p.numeros_factura.length > 0) return p.numeros_factura;
  const una = p?.facturas_info?.[0]?.numero_factura || p?.numero_factura;
  return una ? [una] : [];
};

// Número de factura (sin prefijo) de la fila que contiene el texto buscado
const facturaQueCoincide = (p, texto) => numerosFacturaDe(p)
  .map(extraerNumeroFactura)
  .find(n => n && String(n).includes(texto)) || '';

// Última fecha del evento: la última jornada en un multievento
const fechaFinEvento = (p) => (p?.es_multi_evento && p?.fecha_ultima_jornada ? p.fecha_ultima_jornada : p?.fecha_evento);

// ¿Ya pasó? (multievento: cuando termina su última jornada). Fecha local
const esEventoPasado = (p) => {
  const fin = String(fechaFinEvento(p) || '').slice(0, 10);
  return !!fin && fin < fechaLocalISO();
};

// Fecha del evento en la fila: el rango en un multievento ("17 al 18/11/2026")
const fechaEventoListado = (p) => {
  if (p?.es_multi_evento && p?.fecha_ultima_jornada && p?.fecha_evento) {
    const rango = textoRangoFechas([p.fecha_evento, p.fecha_ultima_jornada]);
    if (rango) return rango;
  }
  return formatFecha(p?.fecha_evento);
};

// Móvil: fecha de la tarjeta con el día de la semana ("jue 23/10/2026") o el
// rango y las jornadas en un multievento
const fechaTarjetaMovil = (p) => {
  if (p?.es_multi_evento && p?.fecha_ultima_jornada && p?.fecha_evento) {
    const n = Number(p.total_jornadas) || (Array.isArray(p.jornadas) ? p.jornadas.length : 0);
    return `${fechaEventoListado(p)}${n ? ` · ${n} jornadas` : ''}`;
  }
  const f = parsearFechaServidor(p?.fecha_evento);
  if (!f) return 'Sin fecha';
  return `${DIAS_SEMANA_CORTOS[f.getDay()]} ${formatFecha(p.fecha_evento)}`;
};

const formatoEurosMovil = (v) => (Number(v) || 0).toLocaleString('es-ES', { style: 'currency', currency: 'EUR' });

const Presupuestos = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // Móvil (< 768 px): lista en tarjetas y alta a pantalla completa. El
  // escritorio no cambia
  const esMovil = useEsMovil();
  const [busquedaMovil, setBusquedaMovil] = useState('');
  const [hojaFiltrosMovil, setHojaFiltrosMovil] = useState(false);
  const [accionesMovil, setAccionesMovil] = useState(null); // presupuesto del menú de la tarjeta
  const [hojaClienteAlta, setHojaClienteAlta] = useState(false);
  const [busquedaClienteAlta, setBusquedaClienteAlta] = useState('');
  const [presupuestos, setPresupuestos] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [menus, setMenus] = useState([]);
  const [platos, setPlatos] = useState([]);
  const [alergenos, setAlergenos] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [limit] = useState(20); // 20 presupuestos por página
  
  // Filtros
  const [filterCliente, setFilterCliente] = useState('');
  const [filterClienteId, setFilterClienteId] = useState(''); // ID del cliente seleccionado
  const [showFilterClienteDropdown, setShowFilterClienteDropdown] = useState(false);
  const [filterNumero, setFilterNumero] = useState('');
  const [filterNumeroSeleccionado, setFilterNumeroSeleccionado] = useState(null); // Número seleccionado del dropdown
  const [showFilterNumeroDropdown, setShowFilterNumeroDropdown] = useState(false);
  const [searchNumeroResults, setSearchNumeroResults] = useState([]); // Resultados de búsqueda de números
  const [searchingNumero, setSearchingNumero] = useState(false);
  const [filterEstado, setFilterEstado] = useState('');
  const [filterFactura, setFilterFactura] = useState('');
  const [filterFacturaSeleccionada, setFilterFacturaSeleccionada] = useState(null);
  const [showFilterFacturaDropdown, setShowFilterFacturaDropdown] = useState(false);
  const [searchFacturaResults, setSearchFacturaResults] = useState([]);
  const [searchingFactura, setSearchingFactura] = useState(false);
  
  // Modal nuevo presupuesto
  const [showNewModal, setShowNewModal] = useState(false);
  const [newPresupuesto, setNewPresupuesto] = useState({
    cliente_id: '',
    persona_contacto_id: '',
    contacto_facturacion_id: '',
    lugar_evento: '',
    fecha_evento: '',
    hora_inicio: '',
    hora_fin: '',
    num_personas: 1,
    observaciones: ''
  });
  // Personas de contacto del cliente seleccionado en el modal
  const [personasContactoCliente, setPersonasContactoCliente] = useState([]);
  // Mini-modal "Nuevo contacto" inline (sin salir del modal de Nuevo Presupuesto)
  const [showNuevoContacto, setShowNuevoContacto] = useState(false);
  const [nuevoContacto, setNuevoContacto] = useState({
    nombre: '', cargo: '', email: '', telefono: '', extension: '', notas: '',
  });
  const [creandoContacto, setCreandoContacto] = useState(false);
  const [errorNuevoContacto, setErrorNuevoContacto] = useState(null);
  
  // Multi-evento
  const [esMultiEvento, setEsMultiEvento] = useState(false);
  const [multiEventoConfig, setMultiEventoConfig] = useState({
    fecha_inicio: '',
    fecha_fin: '',
    excluir_fines_semana: true,
    num_personas_total: 100, // Total a distribuir
    distribucion: 'igual' // 'igual' o 'personalizado'
  });
  const [jornadasPreview, setJornadasPreview] = useState([]);
  
  // Menús y platos seleccionados para el nuevo presupuesto
  const [selectedMenus, setSelectedMenus] = useState([]); // [{menu_id, num_personas}]
  const [activeTabNewPresupuesto, setActiveTabNewPresupuesto] = useState('info'); // 'info', 'menus', 'personalizado'
  
  // Autocompletado de clientes y menús
  const [clienteSearchTerm, setClienteSearchTerm] = useState('');
  const [showClienteDropdown, setShowClienteDropdown] = useState(false);
  const [menuSearchTerm, setMenuSearchTerm] = useState('');

  // Mini-modal para añadir cliente rápido
  const [showAddClienteModal, setShowAddClienteModal] = useState(false);
  const [newClienteQuick, setNewClienteQuick] = useState({
    nombre_empresa: '',
    contacto_principal_nombre: '',
    telefono_principal: '',
    email: ''
  });
  
  // Número de presupuesto correlativo
  const [nextPresupuestoNumber, setNextPresupuestoNumber] = useState(null);
  const [selectedPlatosCustom, setSelectedPlatosCustom] = useState([]); // [{platoId, cantidad}]
  const [customMenuName, setCustomMenuName] = useState('');
  const [customMenuPersonas, setCustomMenuPersonas] = useState('');
  const [customMenuPrecioNew, setCustomMenuPrecioNew] = useState(''); // Precio opcional (permite 0 para placeholder)
  const [platoFiltersNew, setPlatoFiltersNew] = useState({
    searchText: '',
    precioMax: '',
    alergenosEvitar: [],
    categoria: '',
    tiempoMax: '',
    contiene_carne: false,
    derivado_animal: false,
    pasteurizado: false
  });

  // Define fetchData before using it in useEffect
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // Construir params con filtros
      const params = { page: currentPage, limit };
      if (filterClienteId) params.cliente_id = filterClienteId;
      if (filterNumeroSeleccionado) params.numero = filterNumeroSeleccionado;
      if (filterEstado) params.estado = filterEstado;
      if (filterFacturaSeleccionada) params.factura = filterFacturaSeleccionada;
      
      const [presupuestosRes, clientesRes] = await Promise.all([
        presupuestosAPI.getAll(params),
        clientesAPI.getAll()
      ]);
      
      // La respuesta ahora viene con paginación
      if (presupuestosRes.data.presupuestos) {
        setPresupuestos(presupuestosRes.data.presupuestos);
        setTotal(presupuestosRes.data.total);
        setTotalPages(presupuestosRes.data.total_pages);
      } else {
        // Fallback para respuesta sin paginación
        setPresupuestos(presupuestosRes.data);
      }
      
      setClientes(clientesRes.data);
      
      // El backend calcula automáticamente el siguiente número de presupuesto
      // No necesitamos obtener 10,000 registros aquí - eliminado por performance
      setNextPresupuestoNumber(null); // Backend manejará el número automáticamente
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [currentPage, limit, filterClienteId, filterNumeroSeleccionado, filterEstado, filterFacturaSeleccionada]);

  useEffect(() => {
    fetchData();
  }, [fetchData]); // Re-fetch cuando cambie la página o filtros

  // Abrir modal de nuevo presupuesto con cliente preseleccionado vía query params
  // (ej: navegando desde la ficha de un cliente: /presupuestos?nuevo=1&cliente_id=XXX)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const quiereNuevo = params.get('nuevo') === '1';
    const preCliente = params.get('cliente_id');
    if (quiereNuevo && clientes.length > 0) {
      if (preCliente) {
        const c = clientes.find(cl => cl.id === preCliente);
        if (c) {
          setNewPresupuesto(prev => ({ ...prev, cliente_id: c.id }));
          setClienteSearchTerm(c.nombre_empresa || c.nombre || '');
        }
      }
      setShowNewModal(true);
      fetchMenusAndPlatos();
      navigate('/presupuestos', { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, clientes]);

  // Cargar personas de contacto del cliente elegido en el modal de nuevo presupuesto
  useEffect(() => {
    const clienteId = newPresupuesto.cliente_id;
    if (!showNewModal || !clienteId) {
      setPersonasContactoCliente([]);
      return;
    }
    let cancelado = false;
    (async () => {
      try {
        const res = await personasContactoAPI.getAll(clienteId);
        if (!cancelado) setPersonasContactoCliente(res.data || []);
      } catch (error) {
        console.error('Error cargando personas de contacto:', error);
        if (!cancelado) setPersonasContactoCliente([]);
      }
    })();
    return () => { cancelado = true; };
  }, [newPresupuesto.cliente_id, showNewModal]);

  // Re-fetch cuando cambian los filtros (sin debounce - usa selección directa)
  useEffect(() => {
    // Solo hacer fetch si ya se cargaron los datos iniciales
    if (clientes.length > 0) {
      setCurrentPage(1); // Resetear a página 1 al filtrar
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterClienteId, filterNumeroSeleccionado, filterEstado, filterFacturaSeleccionada]);

  // Buscar presupuestos por número (debounce para el dropdown)
  useEffect(() => {
    if (!filterNumero || filterNumero.length < 1) {
      setSearchNumeroResults([]);
      return;
    }
    
    const timer = setTimeout(async () => {
      setSearchingNumero(true);
      try {
        // U-100: el servidor busca en TODO el listado los números que contienen
        // lo escrito (antes, solo entre los 100 primeros)
        const soloDigitos = String(filterNumero).replace(/\D/g, '');
        if (!soloDigitos) {
          setSearchNumeroResults([]);
        } else {
          const response = await presupuestosAPI.getAll({ numero_contiene: soloDigitos, limit: 10 });
          setSearchNumeroResults(response.data.presupuestos || response.data || []);
        }
      } catch (error) {
        console.error('Error buscando presupuestos:', error);
        setSearchNumeroResults([]);
      }
      setSearchingNumero(false);
    }, 300);
    
    return () => clearTimeout(timer);
  }, [filterNumero]);

  // Buscar presupuestos por factura (debounce para el dropdown)
  useEffect(() => {
    if (!filterFactura || filterFactura.length < 1) {
      setSearchFacturaResults([]);
      return;
    }
    
    const timer = setTimeout(async () => {
      setSearchingFactura(true);
      try {
        // Buscar presupuestos que tengan factura con ese número
        const response = await presupuestosAPI.getAll({ factura: filterFactura, limit: 50 });
        const allPresupuestos = response.data.presupuestos || response.data || [];
        
        // Filtrar los que tengan alguna factura (cabecera o jornadas) que
        // contenga el texto buscado
        const filtered = allPresupuestos.filter(p => facturaQueCoincide(p, filterFactura)).slice(0, 10);
        
        setSearchFacturaResults(filtered);
      } catch (error) {
        console.error('Error buscando facturas:', error);
        setSearchFacturaResults([]);
      }
      setSearchingFactura(false);
    }, 300);
    
    return () => clearTimeout(timer);
  }, [filterFactura]);

  // Cerrar dropdown de cliente al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showClienteDropdown && !event.target.closest('.cliente-dropdown-modal')) {
        setShowClienteDropdown(false);
      }
      if (showFilterClienteDropdown && !event.target.closest('.cliente-dropdown-filter')) {
        setShowFilterClienteDropdown(false);
      }
      if (showFilterNumeroDropdown && !event.target.closest('.numero-dropdown-filter')) {
        setShowFilterNumeroDropdown(false);
      }
      if (showFilterFacturaDropdown && !event.target.closest('.factura-dropdown-filter')) {
        setShowFilterFacturaDropdown(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showClienteDropdown, showFilterClienteDropdown, showFilterNumeroDropdown, showFilterFacturaDropdown]);
  
  // Función para generar preview de jornadas cuando se configura multi-evento
  const generarJornadasPreview = () => {
    if (!multiEventoConfig.fecha_inicio || !multiEventoConfig.fecha_fin) {
      setJornadasPreview([]);
      return;
    }
    
    const fechaInicio = new Date(multiEventoConfig.fecha_inicio);
    const fechaFin = new Date(multiEventoConfig.fecha_fin);
    const jornadas = [];
    
    let fechaActual = new Date(fechaInicio);
    while (fechaActual <= fechaFin) {
      const diaSemana = fechaActual.getDay();
      const esFinSemana = diaSemana === 0 || diaSemana === 6;
      
      if (!multiEventoConfig.excluir_fines_semana || !esFinSemana) {
        jornadas.push({
          fecha: fechaActual.toISOString().split('T')[0],
          num_personas: 0 // Se calculará después
        });
      }
      
      fechaActual.setDate(fechaActual.getDate() + 1);
    }
    
    // Distribuir personas
    const totalPersonas = multiEventoConfig.num_personas_total || 0;
    const numJornadas = jornadas.length;
    
    if (numJornadas > 0 && multiEventoConfig.distribucion === 'igual') {
      const personasPorJornada = Math.floor(totalPersonas / numJornadas);
      const residuo = totalPersonas % numJornadas;
      
      jornadas.forEach((j, idx) => {
        j.num_personas = personasPorJornada + (idx < residuo ? 1 : 0);
      });
    }
    
    setJornadasPreview(jornadas);
  };

  // Actualizar preview cuando cambie la config de multi-evento
  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useEffect(() => {
    if (esMultiEvento) {
      generarJornadasPreview();
    } else {
      setJornadasPreview([]);
    }
  }, [esMultiEvento, multiEventoConfig.fecha_inicio, multiEventoConfig.fecha_fin, 
      multiEventoConfig.excluir_fines_semana, multiEventoConfig.num_personas_total,
      multiEventoConfig.distribucion]);

  const fetchMenusAndPlatos = async () => {
    try {
      const [menusRes, platosRes, alergenosRes] = await Promise.all([
        menusAPI.getAll(),
        platosAPI.getAll(),
        alergenosAPI.getAll()
      ]);
      setMenus(menusRes.data.filter(m => m.activo));
      setPlatos(platosRes.data.filter(p => p.activo));
      setAlergenos(alergenosRes.data);
    } catch (error) {
      console.error('Error fetching menus/platos:', error);
    }
  };

  const getClienteName = (clienteId) => {
    const cliente = clientes.find(c => c.id === clienteId);
    return cliente?.nombre_empresa || cliente?.nombre || 'Cliente desconocido';
  };

  const getEstadoBadgeColor = (estado) => {
    const colors = {
      borrador: 'bg-gray-100 text-gray-800',
      enviado: 'bg-blue-100 text-blue-800',
      aceptado: 'bg-green-100 text-green-800',
      rechazado: 'bg-red-100 text-red-800',
      confirmado: 'bg-purple-100 text-purple-800',
      cancelado: 'bg-gray-100 text-gray-800'
    };
    return colors[estado] || colors.borrador;
  };

  const clearFilters = () => {
    setFilterCliente('');
    setFilterClienteId('');
    setFilterNumero('');
    setFilterNumeroSeleccionado(null);
    setFilterEstado('');
    setFilterFactura('');
    setFilterFacturaSeleccionada(null);
  };

  const hasActiveFilters = () => {
    return filterCliente || filterClienteId || filterNumeroSeleccionado || filterEstado || filterFacturaSeleccionada;
  };

  const handleDelete = async (id) => {
    if (window.confirm('¿Estás seguro de eliminar este presupuesto?')) {
      try {
        await presupuestosAPI.delete(id);
        fetchData();
      } catch (error) {
        // Confirmado o con facturas: el servidor dice por qué no se borra (409)
        console.error('Error deleting presupuesto:', error);
        alert(error.response?.data?.detail || 'No se ha podido eliminar el presupuesto.');
      }
    }
  };

  // Solo se ofrece borrar lo que el servidor deja borrar: ni confirmados ni
  // con facturas o proformas (el servidor lo comprueba igualmente)
  const sePuedeBorrar = (p) => {
    if (!p) return false;
    if (['confirmado', 'confirmando'].includes(p.estado)) return false;
    if (p.tiene_factura || p.numero_factura || p.factura_id || p.numero_proforma || p.proforma_id) return false;
    if ((Number(p.total_facturado) || 0) > 0) return false;
    return true;
  };

  // No filtrar en frontend - los datos ya vienen paginados del backend
  const filteredPresupuestos = presupuestos;

  // Calculate some numbers for presupuesto
  const getPresupuestoNumber = (presupuesto) => {
    // Prioridad 1: Usar campo numero nuevo (campo principal para numeración correlativa)
    if (presupuesto.numero) {
      return presupuesto.numero;
    }
    // Prioridad 2: Usar numero_presupuesto_legacy si existe (para presupuestos migrados)
    if (presupuesto.numero_presupuesto_legacy) {
      return presupuesto.numero_presupuesto_legacy;
    }
    // Fallback: extraer número del ID (solo para casos muy antiguos)
    const parts = presupuesto.id.split('-');
    return parts[parts.length - 1] || presupuesto.id.substring(presupuesto.id.length - 4);
  };

  const calculateBeneficio = (presupuesto) => {
    // Usar el beneficio calculado por el backend si existe
    if (presupuesto.beneficio !== undefined && presupuesto.beneficio !== null) {
      return presupuesto.beneficio;
    }
    
    // Fallback: calcular basándose en precio_total y coste_total
    const precioTotal = presupuesto.precio_total || presupuesto.importe_total || 0;
    const costeTotal = presupuesto.coste_total || 0;
    
    return precioTotal - costeTotal;
  };

  const getFilteredPlatosNew = () => {
    return platos.filter(plato => {
      if (!plato.activo) return false;
      // Filtro de búsqueda por texto (nombre del plato)
      if (platoFiltersNew.searchText && platoFiltersNew.searchText.trim()) {
        const searchLower = platoFiltersNew.searchText.toLowerCase();
        const nombreLower = plato.nombre.toLowerCase();
        if (!nombreLower.includes(searchLower)) return false;
      }
      if (platoFiltersNew.precioMax && plato.precio_sugerido > parseFloat(platoFiltersNew.precioMax)) return false;
      if (platoFiltersNew.alergenosEvitar.length > 0) {
        const contieneAlergeno = plato.alergenos_ids?.some(alergId => platoFiltersNew.alergenosEvitar.includes(alergId));
        if (contieneAlergeno) return false;
      }
      if (platoFiltersNew.tiempoMax && plato.tiempo_preparacion_min > parseInt(platoFiltersNew.tiempoMax)) return false;
      if (platoFiltersNew.categoria && plato.categoria !== platoFiltersNew.categoria) return false;
      // EVITAR características
      if (platoFiltersNew.contiene_carne && plato.contiene_carne) return false;
      if (platoFiltersNew.derivado_animal && plato.derivado_animal) return false;
      if (platoFiltersNew.pasteurizado && plato.pasteurizado) return false;
      return true;
    });
  };

  const calculateCustomMenuPriceNew = () => {
    return selectedPlatosCustom.reduce((total, { platoId, cantidad }) => {
      const plato = platos.find(p => p.id === platoId);
      const cantidadFinal = cantidad === '' ? 0 : (parseInt(cantidad) || 1);
      // Usar coste real del plato
      return total + ((plato?.coste || 0) * cantidadFinal);
    }, 0);
  };

  // Plato dado de baja: no entra en la línea nueva (si no se encuentra, se deja)
  const platoActivoAlta = (pid) => {
    const p = platos.find(pl => pl.id === pid);
    return !p || p.activo !== false;
  };

  // Vista previa del alta (U-11): las mismas líneas que se van a guardar, con
  // la regla única de importes (precio de venta del catálogo, IVA real de cada
  // menú, cuota redondeada por línea)
  const calculateTotalsForNewPresupuesto = () => {
    const lineas = [];
    selectedMenus.forEach(sm => {
      const menu = menus.find(m => m.id === sm.menu_id);
      if (menu) lineas.push(lineaDesdeMenu(menu, sm.num_personas, { platoActivo: platoActivoAlta }));
    });
    // Menú personalizado: el precio que se guarda (vacío = 0), al 10 %
    if (selectedPlatosCustom.length > 0 || customMenuName || customMenuPrecioNew) {
      lineas.push({
        precio_unitario: customMenuPrecioNew ? (parseFloat(customMenuPrecioNew) || 0) : 0,
        num_personas: customMenuPersonas ? parseInt(customMenuPersonas, 10) : newPresupuesto.num_personas,
      });
    }
    const r = lineasVentaUnDia({ num_personas: newPresupuesto.num_personas, menus_personalizados: lineas });
    return { subtotal: r.base, iva: r.cuota, total: r.total };
  };

  // Crear contacto inline desde el modal de Nuevo Presupuesto
  const handleCrearNuevoContacto = async () => {
    setErrorNuevoContacto(null);
    if (!newPresupuesto.cliente_id) {
      setErrorNuevoContacto('Selecciona el cliente primero');
      return;
    }
    const nombre = (nuevoContacto.nombre || '').trim();
    if (!nombre) {
      setErrorNuevoContacto('El nombre es obligatorio');
      return;
    }
    setCreandoContacto(true);
    try {
      const res = await personasContactoAPI.create({
        cliente_id: newPresupuesto.cliente_id,
        nombre,
        cargo: (nuevoContacto.cargo || '').trim() || null,
        email: (nuevoContacto.email || '').trim() || null,
        telefono: (nuevoContacto.telefono || '').trim() || null,
        extension: (nuevoContacto.extension || '').trim() || null,
        notas: (nuevoContacto.notas || '').trim() || null,
        es_principal: false,
      });
      const nuevo = res.data;
      // Refrescar lista + autoseleccionar como contacto del evento
      setPersonasContactoCliente((prev) => [...prev, nuevo]);
      setNewPresupuesto((p) => ({
        ...p,
        persona_contacto_id: nuevo.id,
        // Si no había seleccionado todavía contacto de facturación, lo dejamos vacío
        // (= "mismo que el del evento"). Si ya tenía, lo respetamos.
      }));
      // Reset y cierre
      setNuevoContacto({ nombre: '', cargo: '', email: '', telefono: '', extension: '', notas: '' });
      setShowNuevoContacto(false);
    } catch (err) {
      console.error('Error creando contacto:', err);
      setErrorNuevoContacto(err?.response?.data?.detail || 'Error creando el contacto');
    } finally {
      setCreandoContacto(false);
    }
  };

  // Personas del alta (evento de un día): los menús elegidos que tenían las
  // personas anteriores pasan a las nuevas (los que tenían otro número se dejan)
  const cambiarPersonasAlta = (nuevo) => {
    const anterior = newPresupuesto.num_personas;
    setNewPresupuesto(prev => ({ ...prev, num_personas: nuevo }));
    setSelectedMenus(prev => prev.map(sm => (sm.num_personas === anterior ? { ...sm, num_personas: nuevo } : sm)));
  };

  const handleCloseNewModal = () => {
    setShowNewModal(false);
    setClienteSearchTerm('');
    setMenuSearchTerm('');
    setNewPresupuesto({
      cliente_id: '',
      persona_contacto_id: '',
      contacto_facturacion_id: '',
      lugar_evento: '',
      fecha_evento: '',
      hora_inicio: '',
      hora_fin: '',
      num_personas: 1,
      observaciones: ''
    });
    setSelectedMenus([]);
    setSelectedPlatosCustom([]);
    setCustomMenuName('');
    setCustomMenuPersonas('');
    setActiveTabNewPresupuesto('info');
  };
  
  // Función para añadir cliente rápido
  const handleAddClienteQuick = async () => {
    if (!newClienteQuick.nombre_empresa) {
      alert('El nombre de la empresa es obligatorio');
      return;
    }
    
    try {
      const response = await clientesAPI.create({
        ...newClienteQuick,
        estado: 'activo'
      });
      
      // Actualizar lista de clientes
      const clientesRes = await clientesAPI.getAll();
      setClientes(clientesRes.data);
      
      // Seleccionar el nuevo cliente
      setNewPresupuesto({ ...newPresupuesto, cliente_id: response.data.id, persona_contacto_id: '', contacto_facturacion_id: '' });
      setClienteSearchTerm(newClienteQuick.nombre_empresa);
      
      // Cerrar modal y resetear
      setShowAddClienteModal(false);
      setNewClienteQuick({
        nombre_empresa: '',
        contacto_principal_nombre: '',
        telefono_principal: '',
        email: ''
      });
      
      alert('Cliente añadido correctamente');
    } catch (error) {
      console.error('Error al añadir cliente:', error);
      alert('Error al añadir el cliente');
    }
  };
  
  // Funciones para reordenar menús
  const moveMenuUp = (index) => {
    if (index === 0) return;
    const newMenus = [...selectedMenus];
    [newMenus[index - 1], newMenus[index]] = [newMenus[index], newMenus[index - 1]];
    setSelectedMenus(newMenus);
  };

  const moveMenuDown = (index) => {
    if (index === selectedMenus.length - 1) return;
    const newMenus = [...selectedMenus];
    [newMenus[index], newMenus[index + 1]] = [newMenus[index + 1], newMenus[index]];
    setSelectedMenus(newMenus);
  };

  const handleCreatePresupuesto = async () => {
    // Validaciones diferentes para multi-evento
    if (esMultiEvento) {
      if (!newPresupuesto.cliente_id || !multiEventoConfig.fecha_inicio || !multiEventoConfig.fecha_fin) {
        alert('Por favor completa los campos obligatorios: Cliente, Fecha Inicio y Fecha Fin');
        return;
      }
      if (jornadasPreview.length === 0) {
        alert('No hay jornadas generadas. Verifica las fechas seleccionadas.');
        return;
      }
      
      // Validar distribución de personas
      const totalAsignado = jornadasPreview.reduce((sum, j) => sum + j.num_personas, 0);
      if (totalAsignado > multiEventoConfig.num_personas_total) {
        alert(`Error: Has asignado ${totalAsignado} personas pero solo hay ${multiEventoConfig.num_personas_total} contratadas. Ajusta la distribución.`);
        return;
      }
      if (totalAsignado < multiEventoConfig.num_personas_total) {
        const confirmar = window.confirm(`Has asignado ${totalAsignado} de ${multiEventoConfig.num_personas_total} personas. ¿Deseas continuar igualmente?`);
        if (!confirmar) return;
      }
    } else {
      if (!newPresupuesto.cliente_id || !newPresupuesto.fecha_evento || !newPresupuesto.lugar_evento) {
        alert('Por favor completa los campos obligatorios: Cliente, Fecha del Evento y Lugar');
        return;
      }
    }

    // Validar contacto del evento (obligatorio salvo que el cliente no tenga ningún contacto)
    const clienteSel = clientes.find(c => c.id === newPresupuesto.cliente_id);
    const tienePrincipal = !!(clienteSel && (clienteSel.persona_contacto || clienteSel.email || clienteSel.telefono));
    const sinContactos = !tienePrincipal && personasContactoCliente.length === 0;
    if (!sinContactos && !newPresupuesto.persona_contacto_id) {
      alert('Selecciona el contacto del evento. Así evitamos contactar o facturar a la persona equivocada.');
      return;
    }
    // El contacto de facturación, si se deja vacío, hereda el del evento
    const contactoFacturacionId = newPresupuesto.contacto_facturacion_id || newPresupuesto.persona_contacto_id || '';

    try {
      // Get user from localStorage
      const userStr = localStorage.getItem('user');
      const user = userStr ? JSON.parse(userStr) : null;

      // Construir menus_personalizados
      const menusPersonalizados = [];
      
      // Añadir menús existentes seleccionados: la misma línea que "Añadir
      // menú" de la ficha (U-11): precio de venta del catálogo, nunca el
      // coste; IVA del catálogo o 10; platos y unidades por plato
      selectedMenus.forEach(sm => {
        const menu = menus.find(m => m.id === sm.menu_id);
        if (menu) {
          menusPersonalizados.push(lineaDesdeMenu(menu, sm.num_personas, { platoActivo: platoActivoAlta }));
        }
      });
      
      // Añadir menú personalizado si existe alguna configuración
      // Permite menús placeholder sin platos ni precio
      if (selectedPlatosCustom.length > 0 || customMenuName || customMenuPrecioNew) {
        const cliente = clientes.find(c => c.id === newPresupuesto.cliente_id);
        const empresaNombre = cliente?.nombre_empresa || 'Cliente';
        const fechaEvento = esMultiEvento ? multiEventoConfig.fecha_inicio : newPresupuesto.fecha_evento;
        const [year, month, day] = fechaEvento.split('-');
        const nombreMenu = customMenuName || `Menú personalizado ${empresaNombre} / ${day}/${month}/${year}`;
        // Permitir precio 0 para menús placeholder
        const precioTotal = customMenuPrecioNew ? parseFloat(customMenuPrecioNew) : 0;
        const personas = customMenuPersonas ? parseInt(customMenuPersonas) : newPresupuesto.num_personas;
        
        menusPersonalizados.push({
          menu_id: `custom-${Date.now()}`,
          nombre_menu: nombreMenu,
          platos_ids: selectedPlatosCustom.map(p => p.platoId),
          platos_cantidades: selectedPlatosCustom,
          num_personas: personas,
          adaptaciones: [],
          es_personalizado: true,
          precio_unitario: precioTotal
        });
      }

      // Calcular totales
      const { subtotal, iva, total } = calculateTotalsForNewPresupuesto();

      let presupuestoData;
      // Coordenadas de la sugerencia de Google: el alta calcula con ellas la ruta
      // desde la sede (obligatoria al crear, U-02)
      const coordenadasLugar = (newPresupuesto.latitud != null && newPresupuesto.longitud != null)
        ? { lat: Number(newPresupuesto.latitud), lng: Number(newPresupuesto.longitud) }
        : null;
      
      if (esMultiEvento) {
        // Construir jornadas para multi-evento
        const jornadas = jornadasPreview.map((jp, idx) => ({
          fecha: jp.fecha,
          hora_inicio: newPresupuesto.hora_inicio || '09:00',
          hora_fin: newPresupuesto.hora_fin || '14:00',
          num_personas: jp.num_personas,
          // Sin lugar propio: la jornada hereda la dirección principal (M-50)
          menus_personalizados: menusPersonalizados.map(m => ({
            ...m,
            num_personas: jp.num_personas // Ajustar personas por jornada
          })),
          observaciones: ''
        }));
        
        presupuestoData = {
          cliente_id: newPresupuesto.cliente_id,
          persona_contacto_id: newPresupuesto.persona_contacto_id || null,
          contacto_facturacion_id: contactoFacturacionId || null,
          lugar_evento: newPresupuesto.lugar_evento || '',
          coordenadas: coordenadasLugar,
          fecha_evento: multiEventoConfig.fecha_inicio, // Primera fecha
          hora_inicio: newPresupuesto.hora_inicio,
          hora_fin: newPresupuesto.hora_fin,
          num_personas: multiEventoConfig.num_personas_total,
          observaciones: newPresupuesto.observaciones,
          created_by: user?.id || 'admin',
          estado: 'borrador',
          menus_personalizados: menusPersonalizados,
          menus_ids: [],
          platos_ids: [],
          extras: {},
          importe_base: subtotal * jornadasPreview.length, // Multiplicar por jornadas
          descuento: 0,
          iva: iva * jornadasPreview.length,
          importe_total: total * jornadasPreview.length,
          pagos: [],
          // Campos multi-evento
          es_multi_evento: true,
          jornadas: jornadas,
          total_jornadas: jornadasPreview.length,
          total_personas_todas_jornadas: jornadasPreview.reduce((sum, j) => sum + j.num_personas, 0),
          fecha_primera_jornada: multiEventoConfig.fecha_inicio,
          fecha_ultima_jornada: multiEventoConfig.fecha_fin
        };
      } else {
        presupuestoData = {
          ...newPresupuesto,
          coordenadas: coordenadasLugar,
          // NO enviar numero - el backend lo calcula automáticamente
          persona_contacto_id: newPresupuesto.persona_contacto_id || null,
          contacto_facturacion_id: contactoFacturacionId || null,
          created_by: user?.id || 'admin',
          estado: 'borrador',
          menus_personalizados: menusPersonalizados,
          menus_ids: [],
          platos_ids: [],
          extras: {},
          importe_base: subtotal,
          descuento: 0,
          iva: iva,
          importe_total: total,
          pagos: []
        };
      }

      const response = await presupuestosAPI.create(presupuestoData);
      setShowNewModal(false);
      // La ruta se calcula en el alta: si Google no la encuentra, el alta sigue
      // y se avisa (la ficha también lo enseña, con el botón Calcular ruta)
      if (response.data?.aviso_ruta) {
        toast({ title: 'Ruta sin calcular', description: response.data.aviso_ruta, variant: 'destructive' });
      }
      
      // Resetear form
      setNewPresupuesto({
        cliente_id: '',
        persona_contacto_id: '',
        contacto_facturacion_id: '',
        lugar_evento: '',
        fecha_evento: '',
        hora_inicio: '',
        hora_fin: '',
        num_personas: 1,
        observaciones: ''
      });
      setSelectedMenus([]);
      setSelectedPlatosCustom([]);
      setCustomMenuName('');
      setCustomMenuPersonas('');
      setActiveTabNewPresupuesto('info');
      setClienteSearchTerm('');
      setMenuSearchTerm('');
      setEsMultiEvento(false);
      setMultiEventoConfig({
        fecha_inicio: '',
        fecha_fin: '',
        excluir_fines_semana: true,
        num_personas_total: 100,
        distribucion: 'igual'
      });
      setJornadasPreview([]);
      
      // Navegar al presupuesto creado para añadir menús (en el móvil la
      // entrada del alta en el historial pasa a ser la ficha: atrás vuelve a la lista)
      navigate(`/presupuestos/${response.data.id}`, esMovil ? { replace: true } : undefined);
    } catch (error) {
      console.error('Error creating presupuesto:', error);
      alert('Error al crear el presupuesto: ' + (error.response?.data?.detail || error.message));
    }
  };

  // ---------------------------------------------------------------------------
  // Móvil (< 768 px). Solo se usa con esMovil: el escritorio no pasa por aquí
  // ---------------------------------------------------------------------------

  // ¿Hay algo escrito en el alta? Para preguntar antes de cerrarla con atrás
  const hayDatosAlta = () => !!(
    newPresupuesto.cliente_id || newPresupuesto.lugar_evento || newPresupuesto.fecha_evento
    || newPresupuesto.hora_inicio || newPresupuesto.hora_fin || newPresupuesto.observaciones
    || (esMultiEvento && (multiEventoConfig.fecha_inicio || multiEventoConfig.fecha_fin))
  );

  const intentarCerrarAltaMovil = () => {
    if (hayDatosAlta() && !window.confirm('¿Salir sin crear el presupuesto? Se perderá lo que has escrito.')) return;
    handleCloseNewModal();
  };

  // Buscador único: solo cifras = número (numero_contiene, con el mismo
  // debounce); texto = clientes que coinciden
  const cambiarBusquedaMovil = (texto) => {
    setBusquedaMovil(texto);
    const limpio = texto.trim();
    setFilterNumeroSeleccionado(null);
    setFilterNumero(/^\d+$/.test(limpio) ? limpio : '');
  };

  const limpiarBusquedaMovil = () => {
    setBusquedaMovil('');
    setFilterNumero('');
    setFilterNumeroSeleccionado(null);
  };

  const elegirClienteFiltroMovil = (cliente) => {
    setFilterClienteId(cliente.id);
    setFilterCliente(cliente.nombre_empresa);
    setShowFilterClienteDropdown(false);
    limpiarBusquedaMovil();
  };

  const quitarClienteFiltroMovil = () => {
    setFilterCliente('');
    setFilterClienteId('');
  };

  const quitarFacturaFiltroMovil = () => {
    setFilterFactura('');
    setFilterFacturaSeleccionada(null);
  };

  const borrarFiltrosMovil = () => {
    clearFilters();
    limpiarBusquedaMovil();
    setHojaFiltrosMovil(false);
  };

  const abrirAltaMovil = () => {
    setShowNewModal(true);
    fetchMenusAndPlatos();
  };

  const tarjetaMovil = (presupuesto) => {
    const numero = getPresupuestoNumber(presupuesto);
    const cliente = presupuesto.cliente_nombre || getClienteName(presupuesto.cliente_id);
    const numeros = numerosFacturaDe(presupuesto);
    const horario = presupuesto.hora_inicio
      ? `${presupuesto.hora_inicio}${presupuesto.hora_fin ? ` a ${presupuesto.hora_fin}` : ''}`
      : '';
    const personas = Number(presupuesto.num_personas) > 0 ? `${presupuesto.num_personas} pers.` : '';
    const linea2 = [fechaTarjetaMovil(presupuesto), personas, horario].filter(Boolean).join(' · ');
    const pago = presupuesto.estado_pago ? normalizarEstadoPago(presupuesto.estado_pago) : null;
    const confirmadoFuturo = presupuesto.estado === 'confirmado' && fechaFinEvento(presupuesto) && !esEventoPasado(presupuesto);
    const pendientes = confirmadoFuturo ? pendientesEventoConfirmado(presupuesto) : [];
    return (
      <div
        key={presupuesto.id}
        className="relative bg-white rounded-xl border border-[#E1E6E3] shadow-sm"
        data-testid={`presupuesto-tarjeta-${presupuesto.id}`}
      >
        <button
          type="button"
          onClick={() => navigate(`/presupuestos/${presupuesto.id}`)}
          className="w-full text-left p-4 pr-12 active:bg-gray-50 rounded-xl"
          data-uso="presupuestos.abrir"
        >
          <div className="flex items-start gap-2">
            <p className="flex-1 min-w-0 text-[15px] font-bold text-[#16221D] leading-snug line-clamp-2 break-words">
              {numero} · {cliente}
            </p>
            <p className="shrink-0 text-[15px] font-bold text-[#16221D] tabular-nums">
              {formatoEurosMovil(presupuesto.importe_total)}
            </p>
          </div>
          <p className="mt-1 text-[13px] text-[#5B6B64] leading-snug">{linea2}</p>
          {presupuesto.lugar_evento && (
            <p className="mt-0.5 text-[13px] text-[#5B6B64] truncate">{presupuesto.lugar_evento}</p>
          )}
          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${getEstadoBadgeColor(presupuesto.estado)}`}>
              {textoEstado(presupuesto.estado)}
            </span>
            {pago && (
              <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${pago.clase}`}>
                {pago.texto}
              </span>
            )}
            {numeros.length > 0 && (
              <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                {numeros.length === 1 ? 'Factura' : 'Facturas'} {numeros.map(n => `#${extraerNumeroFactura(n)}`).join(', ')}
              </span>
            )}
            {confirmadoFuturo && pendientes.length === 0 && (
              <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-emerald-100 text-emerald-700">Listo</span>
            )}
          </div>
          {pendientes.length > 0 && (
            <p className="mt-2 text-xs leading-snug text-amber-800 bg-amber-50 border border-amber-100 rounded-lg px-2 py-1.5">
              <span className="font-semibold">Por hacer: </span>
              {pendientes.map(pd => pd.detalle || pd.texto).join(' · ')}
            </p>
          )}
        </button>
        <button
          type="button"
          onClick={() => setAccionesMovil(presupuesto)}
          className="absolute top-1 right-1 h-11 w-11 rounded-full flex items-center justify-center text-[#5B6B64] active:bg-gray-100"
          aria-label={`Opciones del presupuesto ${numero}`}
          data-testid={`presupuesto-tarjeta-opciones-${presupuesto.id}`}
        >
          <MoreHorizontal className="w-5 h-5" />
        </button>
      </div>
    );
  };

  const renderListaMovil = () => {
    const textoBusqueda = busquedaMovil.trim();
    const modoNumero = /^\d+$/.test(textoBusqueda);
    const textoCliente = !modoNumero ? textoBusqueda.toLowerCase() : '';
    const clientesCoinciden = textoCliente
      ? clientes.filter(c => c.nombre_empresa && c.nombre_empresa.toLowerCase().includes(textoCliente)).slice(0, 8)
      : [];
    const resultadosNumero = modoNumero
      ? searchNumeroResults.filter(p => !filterEstado || p.estado === filterEstado)
      : [];
    const lista = modoNumero ? resultadosNumero : filteredPresupuestos;
    const cargandoLista = modoNumero ? (searchingNumero || filterNumero !== textoBusqueda) : loading;
    const clienteFiltro = filterClienteId ? (filterCliente || getClienteName(filterClienteId)) : '';
    return (
      <div className="pb-28" data-testid="presupuestos-lista-movil">
        <h1 className="text-xl font-bold text-gray-900 mb-3" data-testid="presupuestos-title">Presupuestos</h1>

        {/* Buscador y chips: fijos arriba al desplazar (el scroll es del <main>; 64 px = cabecera del Layout) */}
        <div className="sticky top-[64px] z-20 -mx-4 px-4 pt-1 pb-2 bg-gray-50" data-guia="presupuestos-movil-buscar">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#8A9892] pointer-events-none" />
            <input
              type="text"
              inputMode="search"
              value={busquedaMovil}
              onChange={(e) => cambiarBusquedaMovil(e.target.value)}
              placeholder="Buscar por nº o cliente"
              className={`${CAMPO_MOVIL} pl-10 pr-12`}
              autoCapitalize="none"
              autoCorrect="off"
              enterKeyHint="search"
              aria-label="Buscar por número o cliente"
              data-testid="presupuestos-movil-buscar"
            />
            {busquedaMovil && (
              <button
                type="button"
                onClick={limpiarBusquedaMovil}
                className="absolute right-0 top-0 h-11 w-11 flex items-center justify-center text-[#5B6B64]"
                aria-label="Borrar la búsqueda"
                data-testid="presupuestos-movil-buscar-limpiar"
              >
                <X className="w-5 h-5" />
              </button>
            )}
            {textoCliente && (
              <div
                className="absolute z-30 left-0 right-0 mt-1 bg-white border border-[#E1E6E3] rounded-xl shadow-lg max-h-[60vh] overflow-y-auto"
                data-testid="presupuestos-movil-clientes"
              >
                {clientesCoinciden.length === 0 ? (
                  <p className="px-4 py-3 text-sm text-gray-500">No hay clientes con ese nombre</p>
                ) : clientesCoinciden.map(cliente => (
                  <button
                    key={cliente.id}
                    type="button"
                    onClick={() => elegirClienteFiltroMovil(cliente)}
                    className="w-full min-h-[48px] px-4 py-2 text-left border-b border-[#EEF1EF] last:border-b-0 active:bg-gray-100"
                    data-uso="presupuestos.filtro"
                    data-uso-detalle="cliente"
                  >
                    <span className="block text-[15px] text-[#16221D] leading-snug">{cliente.nombre_empresa}</span>
                    {cliente.persona_contacto && (
                      <span className="block text-[13px] text-[#5B6B64]">{cliente.persona_contacto}</span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Chips de estado (uno a la vez) y Filtros */}
          <div className="mt-2 flex items-center gap-2">
            <div className="flex-1 min-w-0 flex gap-2 overflow-x-auto -mr-1 pr-1" style={{ scrollbarWidth: 'none' }} data-testid="presupuestos-movil-estados">
              {[{ valor: '', texto: 'Todos' }, ...ESTADOS_FILTRO].map(e => {
                const activo = filterEstado === e.valor;
                return (
                  <button
                    key={e.valor || 'todos'}
                    type="button"
                    onClick={() => setFilterEstado(e.valor)}
                    className={`shrink-0 h-10 px-4 rounded-full text-sm font-medium border whitespace-nowrap ${
                      activo ? 'bg-duetto-primary text-white border-duetto-primary' : 'bg-white text-[#16221D] border-[#CBD3CF]'
                    }`}
                    aria-pressed={activo}
                    data-uso="presupuestos.filtro"
                    data-uso-detalle="estado"
                    data-testid={`presupuestos-movil-estado-${e.valor || 'todos'}`}
                  >
                    {e.texto}
                  </button>
                );
              })}
            </div>
            <button
              type="button"
              onClick={() => setHojaFiltrosMovil(true)}
              className="relative shrink-0 h-10 px-3 rounded-full border border-[#CBD3CF] bg-white text-sm font-medium text-[#16221D] flex items-center gap-1.5"
              data-testid="presupuestos-movil-filtros"
            >
              <SlidersHorizontal className="w-4 h-4" />
              Filtros
              {filterFacturaSeleccionada && (
                <span className="absolute top-1 right-1 w-2.5 h-2.5 rounded-full bg-duetto-primary" aria-label="Filtro de factura activo" />
              )}
            </button>
          </div>

          {/* Filtros activos que no se ven en los chips */}
          {(clienteFiltro || filterFacturaSeleccionada) && (
            <div className="mt-2 flex flex-wrap gap-2">
              {clienteFiltro && (
                <span className="inline-flex items-center max-w-full h-9 pl-3 rounded-full bg-emerald-50 border border-emerald-200 text-sm text-[#11704F]" data-testid="presupuestos-movil-chip-cliente">
                  <span className="truncate">Cliente: {clienteFiltro}</span>
                  <button type="button" onClick={quitarClienteFiltroMovil} className="h-9 w-10 shrink-0 flex items-center justify-center" aria-label="Quitar el filtro de cliente">
                    <X className="w-4 h-4" />
                  </button>
                </span>
              )}
              {filterFacturaSeleccionada && (
                <span className="inline-flex items-center h-9 pl-3 rounded-full bg-emerald-50 border border-emerald-200 text-sm text-[#11704F]" data-testid="presupuestos-movil-chip-factura">
                  <span>Factura: #{filterFacturaSeleccionada}</span>
                  <button type="button" onClick={quitarFacturaFiltroMovil} className="h-9 w-10 shrink-0 flex items-center justify-center" aria-label="Quitar el filtro de factura">
                    <X className="w-4 h-4" />
                  </button>
                </span>
              )}
            </div>
          )}
        </div>

        {modoNumero && (
          <p className="mt-2 mb-2 text-[13px] text-[#5B6B64]">
            Presupuestos con «{textoBusqueda}» en el número{filterEstado ? ` · ${textoEstado(filterEstado)}` : ''}
          </p>
        )}

        <div className="mt-2 space-y-3">
          {cargandoLista ? (
            <p className="py-10 text-center text-gray-500">Cargando...</p>
          ) : lista.length === 0 ? (
            <p className="py-10 text-center text-gray-500 text-sm px-4">
              {modoNumero
                ? `No hay presupuestos con «${textoBusqueda}» en el número.`
                : hasActiveFilters()
                  ? 'No hay presupuestos con estos filtros.'
                  : 'Todavía no hay presupuestos.'}
            </p>
          ) : (
            lista.map(p => tarjetaMovil(p))
          )}
        </div>

        {/* Paginación del servidor (no aplica a la búsqueda por número) */}
        {!modoNumero && !loading && totalPages > 1 && (
          <div className="mt-4 flex flex-wrap items-center justify-between gap-2" data-testid="presupuestos-movil-paginacion">
            <button
              type="button"
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className="h-11 px-4 rounded-lg border border-[#CBD3CF] bg-white text-[15px] font-medium text-[#16221D] disabled:opacity-40"
            >
              Anterior
            </button>
            <span className="text-sm text-[#5B6B64]">Página {currentPage} de {totalPages}</span>
            <button
              type="button"
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
              className="h-11 px-4 rounded-lg border border-[#CBD3CF] bg-white text-[15px] font-medium text-[#16221D] disabled:opacity-40"
            >
              Siguiente
            </button>
          </div>
        )}

        {/* Nuevo: botón flotante abajo a la derecha, encima de la zona segura */}
        {!showNewModal && (
          <button
            type="button"
            onClick={abrirAltaMovil}
            className="fixed right-4 z-30 h-14 pl-5 pr-6 rounded-full bg-[#1A9169] text-white text-base font-semibold shadow-lg shadow-black/20 flex items-center gap-2 active:bg-[#11704F]"
            style={{ bottom: 'calc(env(safe-area-inset-bottom) + 16px)' }}
            data-uso="presupuestos.nuevo"
            data-guia="presupuestos-movil-nuevo"
            data-testid="presupuestos-movil-nuevo"
          >
            <Plus className="w-5 h-5" />
            Nuevo
          </button>
        )}

        {/* Filtros que no caben: factura y borrar filtros */}
        <HojaMovil
          abierta={hojaFiltrosMovil}
          onCerrar={() => setHojaFiltrosMovil(false)}
          titulo="Filtros"
          clave="presupuestos-filtros"
          testid="presupuestos-movil-hoja-filtros"
          pie={(
            <div className="flex gap-2">
              <button
                type="button"
                onClick={borrarFiltrosMovil}
                disabled={!hasActiveFilters() && !busquedaMovil}
                className="flex-1 h-11 rounded-lg border border-red-200 text-red-600 text-[15px] font-medium disabled:opacity-40"
                data-uso="presupuestos.filtro"
                data-uso-detalle="borrar"
                data-testid="presupuestos-movil-borrar-filtros"
              >
                Borrar filtros
              </button>
              <button
                type="button"
                onClick={() => setHojaFiltrosMovil(false)}
                className="flex-1 h-11 rounded-lg bg-duetto-primary text-white text-[15px] font-semibold"
              >
                Ver resultados
              </button>
            </div>
          )}
        >
          <div className="px-5 py-4">
            <label className={ETIQUETA_MOVIL} htmlFor="presupuestos-movil-factura">Factura</label>
            <div className="relative">
              <input
                id="presupuestos-movil-factura"
                type="text"
                inputMode="numeric"
                value={filterFactura}
                onChange={(e) => {
                  setFilterFactura(e.target.value);
                  setShowFilterFacturaDropdown(true);
                  if (!e.target.value) setFilterFacturaSeleccionada(null);
                }}
                placeholder="Nº de factura"
                className={`${CAMPO_MOVIL} pr-12`}
                data-testid="presupuestos-movil-factura"
              />
              {filterFactura && (
                <button
                  type="button"
                  onClick={quitarFacturaFiltroMovil}
                  className="absolute right-0 top-0 h-11 w-11 flex items-center justify-center text-[#5B6B64]"
                  aria-label="Borrar la factura"
                >
                  <X className="w-5 h-5" />
                </button>
              )}
            </div>
            {filterFacturaSeleccionada && filterFactura === filterFacturaSeleccionada && (
              <p className="mt-2 text-[13px] text-[#11704F]">Filtrando por la factura #{filterFacturaSeleccionada}.</p>
            )}
          </div>
          {filterFactura && filterFactura !== filterFacturaSeleccionada && (
            <div className="border-t border-[#EEF1EF]" data-testid="presupuestos-movil-facturas">
              {searchingFactura ? (
                <p className="px-5 py-3 text-sm text-gray-500">Buscando...</p>
              ) : searchFacturaResults.length === 0 ? (
                <p className="px-5 py-3 text-sm text-gray-500">No se encontró factura con ese número</p>
              ) : searchFacturaResults.map((p) => {
                const numFacturaSimple = facturaQueCoincide(p, filterFactura)
                  || extraerNumeroFactura(numerosFacturaDe(p)[0] || '');
                return (
                  <FilaHojaMovil
                    key={p.id}
                    texto={`Factura #${numFacturaSimple}`}
                    descripcion={`Presupuesto ${p.numero} · ${p.cliente_nombre || ''}`}
                    onClick={() => {
                      setFilterFactura(numFacturaSimple);
                      setFilterFacturaSeleccionada(numFacturaSimple);
                      setShowFilterFacturaDropdown(false);
                      setHojaFiltrosMovil(false);
                    }}
                    data-uso="presupuestos.filtro"
                    data-uso-detalle="factura"
                  />
                );
              })}
            </div>
          )}
        </HojaMovil>

        {/* Menú de la tarjeta: abrir y eliminar */}
        <HojaMovil
          abierta={!!accionesMovil}
          onCerrar={() => setAccionesMovil(null)}
          titulo={accionesMovil ? `${getPresupuestoNumber(accionesMovil)} · ${accionesMovil.cliente_nombre || getClienteName(accionesMovil.cliente_id)}` : ''}
          clave="presupuestos-acciones"
          testid="presupuestos-movil-hoja-acciones"
        >
          {accionesMovil && (
            <div className="py-1">
              <FilaHojaMovil
                icono={ExternalLink}
                texto="Abrir"
                onClick={() => {
                  const id = accionesMovil.id;
                  setAccionesMovil(null);
                  // replace: la entrada de la hoja en el historial pasa a ser la ficha
                  navigate(`/presupuestos/${id}`, { replace: true });
                }}
                data-uso="presupuestos.abrir"
                data-testid="presupuestos-movil-accion-abrir"
              />
              {sePuedeBorrar(accionesMovil) && (
                <>
                  <SeparadorHojaMovil />
                  <FilaHojaMovil
                    icono={Trash2}
                    texto="Eliminar"
                    tono="peligro"
                    onClick={() => {
                      const id = accionesMovil.id;
                      setAccionesMovil(null);
                      handleDelete(id);
                    }}
                    data-testid="presupuestos-movil-accion-eliminar"
                  />
                </>
              )}
            </div>
          )}
        </HojaMovil>
      </div>
    );
  };

  // Alta a pantalla completa en el móvil: los mismos campos y handlers que el
  // modal del escritorio, en una columna y con un solo scroll
  const renderAltaMovil = () => {
    const totalAsignado = jornadasPreview.reduce((sum, j) => sum + j.num_personas, 0);
    const totalContratado = multiEventoConfig.num_personas_total;
    const colorTotal = totalAsignado === totalContratado
      ? 'text-green-700'
      : totalAsignado > totalContratado ? 'text-red-600' : 'text-orange-600';
    const clienteSel = clientes.find(c => c.id === newPresupuesto.cliente_id);
    const tienePrincipal = !!(clienteSel && (clienteSel.persona_contacto || clienteSel.email || clienteSel.telefono));
    const sinContactos = !tienePrincipal && personasContactoCliente.length === 0;
    const nombrePrincipal = clienteSel ? (clienteSel.persona_contacto || clienteSel.nombre_empresa || 'Contacto principal') : '';
    const opcionesContacto = [...personasContactoCliente].sort((a, b) => (a.nombre || '').localeCompare(b.nombre || ''));
    return (
      <div
        className="fixed inset-0 z-50 h-[100dvh] bg-white flex flex-col"
        role="dialog"
        aria-modal="true"
        aria-label="Nuevo presupuesto"
        data-testid="alta-presupuesto-movil"
      >
        {/* Cabecera fija */}
        <div
          className="shrink-0 flex items-center gap-1 pl-1 pr-4 border-b border-[#EEF1EF] bg-white"
          style={{ paddingTop: 'env(safe-area-inset-top)' }}
        >
          <button
            type="button"
            onClick={intentarCerrarAltaMovil}
            className="h-11 w-11 shrink-0 rounded-full flex items-center justify-center text-[#5B6B64] active:bg-gray-100"
            aria-label="Cerrar"
            data-testid="alta-presupuesto-movil-cerrar"
          >
            <X className="w-6 h-6" />
          </button>
          <h2 className="flex-1 min-w-0 py-3 text-lg font-bold text-[#16221D] truncate">Nuevo presupuesto</h2>
        </div>

        {/* Cuerpo: un solo scroll */}
        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-4 pt-3 pb-6 space-y-4">
          <p className="text-[13px] text-[#5B6B64]">Los menús se añaden en la ficha, después de crear el presupuesto.</p>

          {/* Cliente */}
          <section className="space-y-3">
            <h3 className={SECCION_MOVIL}>Cliente</h3>
            <div>
              <span className={ETIQUETA_MOVIL}>Cliente <span className="text-red-500">*</span></span>
              <button
                type="button"
                onClick={() => {
                  setBusquedaClienteAlta('');
                  setHojaClienteAlta(true);
                }}
                className={`${CAMPO_MOVIL} flex items-center justify-between gap-2 text-left`}
                data-testid="alta-movil-cliente"
              >
                <span className={`truncate ${newPresupuesto.cliente_id ? '' : 'text-[#8A9892]'}`}>
                  {newPresupuesto.cliente_id ? (clienteSearchTerm || getClienteName(newPresupuesto.cliente_id)) : 'Elige un cliente'}
                </span>
                <ChevronRight className="w-5 h-5 shrink-0 text-[#8A9892]" />
              </button>
            </div>

            {newPresupuesto.cliente_id && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 space-y-3" data-testid="alta-movil-contactos">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm font-semibold text-gray-800">Contactos del presupuesto</p>
                  <button
                    type="button"
                    onClick={() => {
                      setErrorNuevoContacto(null);
                      setNuevoContacto({ nombre: '', cargo: '', email: '', telefono: '', extension: '', notas: '' });
                      setShowNuevoContacto(true);
                    }}
                    className="h-11 px-3 shrink-0 rounded-lg text-sm font-semibold text-duetto-primary bg-white border border-emerald-200 active:bg-emerald-100 inline-flex items-center gap-1"
                    data-testid="alta-movil-nuevo-contacto"
                  >
                    <Plus className="w-4 h-4" />
                    Nuevo contacto
                  </button>
                </div>
                {sinContactos ? (
                  <p className="text-[13px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                    Este cliente no tiene contactos ni email o teléfono guardados. Pulsa <strong>Nuevo contacto</strong> para añadir uno sin salir de aquí.
                  </p>
                ) : (
                  <>
                    <div>
                      <label className={ETIQUETA_MOVIL} htmlFor="alta-movil-contacto-evento">
                        Contacto del evento <span className="text-red-500">*</span>
                      </label>
                      <select
                        id="alta-movil-contacto-evento"
                        value={newPresupuesto.persona_contacto_id}
                        onChange={(e) => setNewPresupuesto({ ...newPresupuesto, persona_contacto_id: e.target.value })}
                        className={CAMPO_MOVIL}
                      >
                        <option value="">Selecciona contacto</option>
                        {tienePrincipal && (
                          <option value="principal">{nombrePrincipal} (contacto principal)</option>
                        )}
                        {opcionesContacto.map(p => (
                          <option key={p.id} value={p.id}>{p.nombre}{p.cargo ? ` - ${p.cargo}` : ''}</option>
                        ))}
                      </select>
                      <p className="text-xs text-gray-500 mt-1">Persona presente el día del evento.</p>
                    </div>
                    <div>
                      <label className={ETIQUETA_MOVIL} htmlFor="alta-movil-contacto-facturacion">Contacto de facturación</label>
                      <select
                        id="alta-movil-contacto-facturacion"
                        value={newPresupuesto.contacto_facturacion_id}
                        onChange={(e) => setNewPresupuesto({ ...newPresupuesto, contacto_facturacion_id: e.target.value })}
                        className={CAMPO_MOVIL}
                      >
                        <option value="">Mismo que el del evento</option>
                        {tienePrincipal && (
                          <option value="principal">{nombrePrincipal} (contacto principal)</option>
                        )}
                        {opcionesContacto.map(p => (
                          <option key={p.id} value={p.id}>{p.nombre}{p.cargo ? ` - ${p.cargo}` : ''}</option>
                        ))}
                      </select>
                      <p className="text-xs text-gray-500 mt-1">A quién se le envía la factura.</p>
                    </div>
                  </>
                )}
              </div>
            )}
          </section>

          {/* Evento */}
          <section className="space-y-3">
            <h3 className={SECCION_MOVIL}>Evento</h3>
            <label className="flex items-center gap-3 bg-indigo-50 border border-indigo-200 rounded-lg p-3 cursor-pointer" data-testid="alta-movil-multievento">
              <span className="flex-1 min-w-0">
                <span className="block text-[15px] font-medium text-gray-900">Presupuesto multievento</span>
                <span className="block text-[13px] text-gray-500">Para eventos que se repiten en varios días</span>
              </span>
              <span className="relative inline-flex items-center shrink-0">
                <input
                  type="checkbox"
                  checked={esMultiEvento}
                  onChange={(e) => setEsMultiEvento(e.target.checked)}
                  className="sr-only peer"
                />
                <span className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></span>
              </span>
            </label>

            {esMultiEvento ? (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={ETIQUETA_MOVIL} htmlFor="alta-movil-fecha-inicio">Inicio <span className="text-red-500">*</span></label>
                    <input
                      id="alta-movil-fecha-inicio"
                      type="date"
                      value={multiEventoConfig.fecha_inicio}
                      onChange={(e) => setMultiEventoConfig({ ...multiEventoConfig, fecha_inicio: e.target.value })}
                      min={fechaLocalISO()}
                      className={CAMPO_MOVIL}
                    />
                  </div>
                  <div>
                    <label className={ETIQUETA_MOVIL} htmlFor="alta-movil-fecha-fin">Fin <span className="text-red-500">*</span></label>
                    <input
                      id="alta-movil-fecha-fin"
                      type="date"
                      value={multiEventoConfig.fecha_fin}
                      onChange={(e) => setMultiEventoConfig({ ...multiEventoConfig, fecha_fin: e.target.value })}
                      min={multiEventoConfig.fecha_inicio || fechaLocalISO()}
                      className={CAMPO_MOVIL}
                    />
                  </div>
                </div>
                <label className="flex items-center gap-3 min-h-[44px] cursor-pointer">
                  <input
                    type="checkbox"
                    checked={multiEventoConfig.excluir_fines_semana}
                    onChange={(e) => setMultiEventoConfig({ ...multiEventoConfig, excluir_fines_semana: e.target.checked })}
                    className="w-6 h-6 shrink-0 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                  />
                  <span className="text-[15px] text-gray-700">Excluir fines de semana</span>
                </label>
                <div>
                  <label className={ETIQUETA_MOVIL} htmlFor="alta-movil-total-personas">Total de personas a distribuir</label>
                  <input
                    id="alta-movil-total-personas"
                    type="number"
                    inputMode="numeric"
                    value={multiEventoConfig.num_personas_total}
                    onChange={(e) => setMultiEventoConfig({ ...multiEventoConfig, num_personas_total: parseInt(e.target.value) || 0 })}
                    min="1"
                    className={CAMPO_MOVIL}
                  />
                </div>
                <div>
                  <label className={ETIQUETA_MOVIL} htmlFor="alta-movil-distribucion">Distribución</label>
                  <select
                    id="alta-movil-distribucion"
                    value={multiEventoConfig.distribucion}
                    onChange={(e) => setMultiEventoConfig({ ...multiEventoConfig, distribucion: e.target.value })}
                    className={CAMPO_MOVIL}
                  >
                    <option value="igual">Igual entre días</option>
                    <option value="personalizado">Personalizar después</option>
                  </select>
                </div>

                {jornadasPreview.length > 0 && (
                  <div className="rounded-lg border border-[#E1E6E3]" data-testid="alta-movil-jornadas">
                    <div className="flex items-center justify-between gap-2 px-3 py-2 bg-gray-50 rounded-t-lg border-b border-[#E1E6E3]">
                      <span className="text-sm font-medium text-gray-700">{jornadasPreview.length} jornadas</span>
                      <span className={`text-sm font-semibold ${colorTotal}`}>
                        {totalAsignado} de {totalContratado} personas
                      </span>
                    </div>
                    {jornadasPreview.map((jp, idx) => {
                      const fecha = new Date(jp.fecha);
                      return (
                        <div key={jp.fecha || idx} className="flex items-center gap-3 px-3 py-2 border-b border-[#EEF1EF] last:border-b-0">
                          <span className="flex-1 min-w-0 text-[15px] text-gray-900">
                            <span className="font-medium">Día {idx + 1}</span>
                            <span className="text-[#5B6B64]"> · {DIAS_SEMANA_CORTOS[fecha.getUTCDay()]} {fecha.getUTCDate()}/{fecha.getUTCMonth() + 1}/{fecha.getUTCFullYear()}</span>
                          </span>
                          <input
                            type="number"
                            inputMode="numeric"
                            min="0"
                            value={jp.num_personas}
                            onChange={(e) => {
                              const newJornadas = [...jornadasPreview];
                              newJornadas[idx].num_personas = parseInt(e.target.value) || 0;
                              setJornadasPreview(newJornadas);
                            }}
                            className={`${CAMPO_MOVIL} w-24 text-center`}
                            aria-label={`Personas del día ${idx + 1}`}
                          />
                        </div>
                      );
                    })}
                    {totalAsignado !== totalContratado && (
                      <p className={`px-3 py-2 text-[13px] ${colorTotal}`}>
                        {totalAsignado > totalContratado
                          ? `Excedes ${totalAsignado - totalContratado} personas del total contratado.`
                          : `Faltan ${totalContratado - totalAsignado} personas por asignar.`}
                      </p>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                <div>
                  <label className={ETIQUETA_MOVIL} htmlFor="alta-movil-fecha">Fecha del evento <span className="text-red-500">*</span></label>
                  {/* Mismo valor que guarda el DatePicker del escritorio: 'YYYY-MM-DD' */}
                  <input
                    id="alta-movil-fecha"
                    type="date"
                    value={newPresupuesto.fecha_evento}
                    onChange={(e) => setNewPresupuesto({ ...newPresupuesto, fecha_evento: e.target.value })}
                    className={CAMPO_MOVIL}
                    data-testid="alta-movil-fecha"
                  />
                </div>
                <div>
                  <label className={ETIQUETA_MOVIL} htmlFor="alta-movil-personas">Número de personas <span className="text-red-500">*</span></label>
                  <input
                    id="alta-movil-personas"
                    type="number"
                    inputMode="numeric"
                    min="1"
                    value={newPresupuesto.num_personas || ''}
                    onChange={(e) => cambiarPersonasAlta(e.target.value === '' ? '' : (parseInt(e.target.value) || 1))}
                    onBlur={(e) => { if (e.target.value === '') cambiarPersonasAlta(1); }}
                    className={CAMPO_MOVIL}
                  />
                </div>
              </div>
            )}
          </section>

          {/* Lugar y horario */}
          <section className="space-y-3">
            <h3 className={SECCION_MOVIL}>Lugar y horario</h3>
            <div>
              <label className={ETIQUETA_MOVIL} htmlFor="address-autocomplete-modal">
                Lugar del evento {!esMultiEvento && <span className="text-red-500">*</span>}
              </label>
              <DireccionAutocomplete
                id="address-autocomplete-modal"
                value={newPresupuesto.lugar_evento}
                onChange={(lugar) => setNewPresupuesto(prev => ({ ...prev, lugar_evento: lugar, latitud: null, longitud: null }))}
                onSelect={(lugar) => setNewPresupuesto(prev => ({
                  ...prev,
                  lugar_evento: lugar.direccion || lugar.nombre,
                  latitud: lugar.lat,
                  longitud: lugar.lng,
                }))}
                placeholder="Buscar dirección"
                className={`${CAMPO_MOVIL} py-2.5 leading-6`}
              />
              {esMultiEvento && (
                <p className="text-xs text-gray-500 mt-1">Este lugar se aplicará a todas las jornadas (puedes cambiarlo después).</p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={ETIQUETA_MOVIL} htmlFor="alta-movil-hora-inicio">Hora inicio</label>
                <input
                  id="alta-movil-hora-inicio"
                  type="time"
                  value={newPresupuesto.hora_inicio}
                  onChange={(e) => setNewPresupuesto({ ...newPresupuesto, hora_inicio: e.target.value })}
                  className={CAMPO_MOVIL}
                />
              </div>
              <div>
                <label className={ETIQUETA_MOVIL} htmlFor="alta-movil-hora-fin">Hora fin</label>
                <input
                  id="alta-movil-hora-fin"
                  type="time"
                  value={newPresupuesto.hora_fin}
                  onChange={(e) => setNewPresupuesto({ ...newPresupuesto, hora_fin: e.target.value })}
                  className={CAMPO_MOVIL}
                />
              </div>
            </div>
          </section>

          {/* Observaciones */}
          <section className="space-y-3">
            <h3 className={SECCION_MOVIL}>Observaciones</h3>
            <textarea
              value={newPresupuesto.observaciones}
              onChange={(e) => setNewPresupuesto({ ...newPresupuesto, observaciones: e.target.value })}
              placeholder="Notas adicionales..."
              rows="3"
              aria-label="Observaciones"
              className="w-full border border-[#CBD3CF] rounded-lg px-3 py-2 text-base resize-none focus:outline-none focus:ring-2 focus:ring-duetto-primary"
            />
          </section>
        </div>

        {/* Pie fijo */}
        <div
          className="shrink-0 border-t border-[#EEF1EF] bg-white px-4 pt-3 flex gap-3"
          style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 12px)' }}
        >
          <button
            type="button"
            onClick={intentarCerrarAltaMovil}
            className="flex-1 h-12 rounded-lg border border-[#CBD3CF] text-[15px] font-medium text-gray-700 active:bg-gray-50"
            data-testid="alta-presupuesto-movil-cancelar"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleCreatePresupuesto}
            className="flex-[2] h-12 rounded-lg bg-duetto-primary text-white text-[15px] font-semibold active:bg-[#11704F]"
            data-testid="alta-presupuesto-movil-crear"
          >
            Crear presupuesto
          </button>
        </div>
      </div>
    );
  };

  // Hojas del alta en el móvil: elegir cliente, nuevo cliente y nuevo contacto
  const renderHojasAltaMovil = () => {
    const texto = busquedaClienteAlta.trim().toLowerCase();
    const coinciden = clientes
      .filter(c => c.nombre_empresa && (!texto || c.nombre_empresa.toLowerCase().includes(texto)))
      .sort((a, b) => (a.nombre_empresa || '').localeCompare(b.nombre_empresa || ''));
    const visibles = coinciden.slice(0, 50);
    const cerrarNuevoCliente = () => {
      setShowAddClienteModal(false);
      setNewClienteQuick({ nombre_empresa: '', contacto_principal_nombre: '', telefono_principal: '', email: '' });
    };
    return (
      <>
        <HojaMovil
          abierta={hojaClienteAlta}
          onCerrar={() => setHojaClienteAlta(false)}
          titulo="Elegir cliente"
          clave="alta-elegir-cliente"
          testid="alta-movil-hoja-cliente"
          alto
        >
          <div className="sticky top-0 z-10 bg-white px-4 pt-3 pb-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#8A9892] pointer-events-none" />
              <input
                type="text"
                inputMode="search"
                value={busquedaClienteAlta}
                onChange={(e) => setBusquedaClienteAlta(e.target.value)}
                placeholder="Buscar cliente"
                className={`${CAMPO_MOVIL} pl-10`}
                autoFocus
                autoCapitalize="none"
                autoCorrect="off"
                data-testid="alta-movil-buscar-cliente"
              />
            </div>
          </div>
          <FilaHojaMovil
            icono={UserPlus}
            texto="Nuevo cliente"
            tono="principal"
            onClick={() => {
              setHojaClienteAlta(false);
              if (busquedaClienteAlta.trim() && !newClienteQuick.nombre_empresa) {
                setNewClienteQuick(prev => ({ ...prev, nombre_empresa: busquedaClienteAlta.trim() }));
              }
              setShowAddClienteModal(true);
            }}
            data-testid="alta-movil-nuevo-cliente"
          />
          <SeparadorHojaMovil />
          {visibles.length === 0 ? (
            <p className="px-5 py-3 text-sm text-gray-500">No se encontraron clientes</p>
          ) : visibles.map(cliente => (
            <FilaHojaMovil
              key={cliente.id}
              texto={cliente.nombre_empresa}
              descripcion={cliente.contacto_principal_nombre ? `Contacto: ${cliente.contacto_principal_nombre}` : null}
              marcado={newPresupuesto.cliente_id === cliente.id}
              onClick={() => {
                setNewPresupuesto(prev => ({ ...prev, cliente_id: cliente.id, persona_contacto_id: '', contacto_facturacion_id: '' }));
                setClienteSearchTerm(cliente.nombre_empresa);
                setShowClienteDropdown(false);
                setHojaClienteAlta(false);
              }}
              data-cliente-id={cliente.id}
            />
          ))}
          {coinciden.length > visibles.length && (
            <p className="px-5 py-3 text-[13px] text-[#5B6B64]">
              Hay {coinciden.length} clientes. Escribe para buscar.
            </p>
          )}
        </HojaMovil>

        <HojaMovil
          abierta={showAddClienteModal}
          onCerrar={cerrarNuevoCliente}
          titulo="Nuevo cliente"
          clave="alta-nuevo-cliente"
          testid="alta-movil-hoja-nuevo-cliente"
          alto
          pie={(
            <div className="flex gap-3">
              <button type="button" onClick={cerrarNuevoCliente} className="flex-1 h-11 rounded-lg border border-[#CBD3CF] text-[15px] text-gray-700">
                Cancelar
              </button>
              <button type="button" onClick={handleAddClienteQuick} className="flex-[2] h-11 rounded-lg bg-duetto-primary text-white text-[15px] font-semibold" data-testid="alta-movil-nuevo-cliente-guardar">
                Añadir cliente
              </button>
            </div>
          )}
        >
          <div className="px-5 py-4 space-y-4">
            <div>
              <label className={ETIQUETA_MOVIL} htmlFor="alta-movil-nc-empresa">Nombre de la empresa <span className="text-red-500">*</span></label>
              <input
                id="alta-movil-nc-empresa"
                type="text"
                value={newClienteQuick.nombre_empresa}
                onChange={(e) => setNewClienteQuick({ ...newClienteQuick, nombre_empresa: e.target.value })}
                placeholder="Nombre de la empresa"
                autoCapitalize="words"
                className={CAMPO_MOVIL}
              />
            </div>
            <div>
              <label className={ETIQUETA_MOVIL} htmlFor="alta-movil-nc-contacto">Contacto principal</label>
              <input
                id="alta-movil-nc-contacto"
                type="text"
                value={newClienteQuick.contacto_principal_nombre}
                onChange={(e) => setNewClienteQuick({ ...newClienteQuick, contacto_principal_nombre: e.target.value })}
                placeholder="Nombre del contacto"
                autoCapitalize="words"
                autoComplete="name"
                className={CAMPO_MOVIL}
              />
            </div>
            <div>
              <label className={ETIQUETA_MOVIL} htmlFor="alta-movil-nc-telefono">Teléfono</label>
              <input
                id="alta-movil-nc-telefono"
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                value={newClienteQuick.telefono_principal}
                onChange={(e) => setNewClienteQuick({ ...newClienteQuick, telefono_principal: e.target.value })}
                placeholder="+34 600 000 000"
                className={CAMPO_MOVIL}
              />
            </div>
            <div>
              <label className={ETIQUETA_MOVIL} htmlFor="alta-movil-nc-email">Email</label>
              <input
                id="alta-movil-nc-email"
                type="email"
                inputMode="email"
                autoComplete="email"
                autoCapitalize="none"
                autoCorrect="off"
                value={newClienteQuick.email}
                onChange={(e) => setNewClienteQuick({ ...newClienteQuick, email: e.target.value })}
                placeholder="email@empresa.com"
                className={CAMPO_MOVIL}
              />
            </div>
          </div>
        </HojaMovil>

        <HojaMovil
          abierta={showNuevoContacto}
          onCerrar={() => { if (!creandoContacto) setShowNuevoContacto(false); }}
          titulo="Nuevo contacto"
          subtitulo="Se asignará como contacto del evento."
          clave="alta-nuevo-contacto"
          testid="alta-movil-hoja-nuevo-contacto"
          alto
          pie={(
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setShowNuevoContacto(false)}
                disabled={creandoContacto}
                className="flex-1 h-11 rounded-lg border border-[#CBD3CF] text-[15px] text-gray-700 disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleCrearNuevoContacto}
                disabled={creandoContacto || !nuevoContacto.nombre.trim()}
                className="flex-[2] h-11 rounded-lg bg-duetto-primary text-white text-[15px] font-semibold disabled:opacity-50"
                data-testid="alta-movil-nuevo-contacto-guardar"
              >
                {creandoContacto ? 'Guardando...' : 'Guardar y seleccionar'}
              </button>
            </div>
          )}
        >
          <div className="px-5 py-4 space-y-4">
            <div>
              <label className={ETIQUETA_MOVIL} htmlFor="alta-movil-ct-nombre">Nombre <span className="text-red-500">*</span></label>
              <input
                id="alta-movil-ct-nombre"
                type="text"
                value={nuevoContacto.nombre}
                onChange={(e) => setNuevoContacto({ ...nuevoContacto, nombre: e.target.value })}
                placeholder="Ej: Manuel García"
                autoCapitalize="words"
                autoComplete="name"
                className={CAMPO_MOVIL}
              />
            </div>
            <div>
              <label className={ETIQUETA_MOVIL} htmlFor="alta-movil-ct-cargo">Cargo</label>
              <input
                id="alta-movil-ct-cargo"
                type="text"
                value={nuevoContacto.cargo}
                onChange={(e) => setNuevoContacto({ ...nuevoContacto, cargo: e.target.value })}
                placeholder="Ej: Director RRHH"
                autoCapitalize="sentences"
                className={CAMPO_MOVIL}
              />
            </div>
            <div>
              <label className={ETIQUETA_MOVIL} htmlFor="alta-movil-ct-email">Email</label>
              <input
                id="alta-movil-ct-email"
                type="email"
                inputMode="email"
                autoComplete="email"
                autoCapitalize="none"
                autoCorrect="off"
                value={nuevoContacto.email}
                onChange={(e) => setNuevoContacto({ ...nuevoContacto, email: e.target.value })}
                placeholder="email@empresa.com"
                className={CAMPO_MOVIL}
              />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2">
                <label className={ETIQUETA_MOVIL} htmlFor="alta-movil-ct-telefono">Teléfono</label>
                <input
                  id="alta-movil-ct-telefono"
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  value={nuevoContacto.telefono}
                  onChange={(e) => setNuevoContacto({ ...nuevoContacto, telefono: e.target.value })}
                  placeholder="+34 600 000 000"
                  className={CAMPO_MOVIL}
                />
              </div>
              <div>
                <label className={ETIQUETA_MOVIL} htmlFor="alta-movil-ct-extension">Ext.</label>
                <input
                  id="alta-movil-ct-extension"
                  type="text"
                  inputMode="numeric"
                  value={nuevoContacto.extension}
                  onChange={(e) => setNuevoContacto({ ...nuevoContacto, extension: e.target.value })}
                  placeholder="Ext."
                  className={CAMPO_MOVIL}
                  data-testid="nuevo-contacto-extension"
                />
              </div>
            </div>
            <div>
              <label className={ETIQUETA_MOVIL} htmlFor="alta-movil-ct-notas">Notas</label>
              <textarea
                id="alta-movil-ct-notas"
                value={nuevoContacto.notas}
                onChange={(e) => setNuevoContacto({ ...nuevoContacto, notas: e.target.value })}
                placeholder="Horario, preferencias, cómo contactar..."
                rows={2}
                autoCapitalize="sentences"
                className="w-full border border-[#CBD3CF] rounded-lg px-3 py-2 text-base resize-none focus:outline-none focus:ring-2 focus:ring-duetto-primary"
                data-testid="nuevo-contacto-notas"
              />
            </div>
            {errorNuevoContacto && (
              <div className="bg-red-50 border border-red-200 rounded px-3 py-2 text-[13px] text-red-800">
                {errorNuevoContacto}
              </div>
            )}
          </div>
        </HojaMovil>
      </>
    );
  };

  // Atrás del móvil: cierra el alta (preguntando si hay algo escrito)
  useCapaAtras('alta-presupuesto', esMovil && showNewModal, intentarCerrarAltaMovil);

  // En el móvil la lista enseña su propio "Cargando..." sin quitar el buscador
  if (loading && !esMovil) {
    return <div className="flex items-center justify-center h-64">Cargando...</div>;
  }

  return (
    <div>
      {esMovil && renderListaMovil()}
      {!esMovil && (<>
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900" data-testid="presupuestos-title">Presupuestos</h1>
          <p className="text-gray-600 mt-1">Gestión de presupuestos y eventos</p>
        </div>
        <button
          onClick={() => {
            setShowNewModal(true);
            fetchMenusAndPlatos();
          }}
          className="px-4 py-2 bg-duetto-primary text-white rounded-lg hover:bg-opacity-90 transition-colors flex items-center space-x-2"
          data-uso="presupuestos.nuevo"
        >
          <Plus className="w-5 h-5" />
          <span>Nuevo Presupuesto</span>
        </button>
      </div>

      {/* Filters Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">Localizar presupuesto</h3>
          {hasActiveFilters() && (
            <button
              onClick={clearFilters}
              data-uso="presupuestos.filtro"
              data-uso-detalle="borrar"
              className="px-3 py-1 bg-red-500 text-white text-sm rounded-lg hover:bg-red-600 transition-colors flex items-center space-x-1"
            >
              <X className="w-4 h-4" />
              <span>Borrar filtros</span>
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="cliente-dropdown-filter relative">
            <label className="block text-sm font-medium text-gray-700 mb-1">Cliente:</label>
            <input
              type="text"
              placeholder="Buscar cliente..."
              value={filterCliente}
              onChange={(e) => {
                setFilterCliente(e.target.value);
                setShowFilterClienteDropdown(true);
                if (!e.target.value) {
                  setFilterClienteId('');
                }
              }}
              onFocus={() => setShowFilterClienteDropdown(true)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-duetto-primary"
            />
            {showFilterClienteDropdown && filterCliente && (
              <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                {clientes
                  .filter(cliente => 
                    cliente.nombre_empresa && 
                    cliente.nombre_empresa.toLowerCase().includes(filterCliente.toLowerCase())
                  )
                  .slice(0, 15) // Limitar a 15 resultados
                  .map(cliente => (
                    <div
                      key={cliente.id}
                      data-uso="presupuestos.filtro"
                      data-uso-detalle="cliente"
                      onClick={() => {
                        setFilterClienteId(cliente.id);
                        setFilterCliente(cliente.nombre_empresa);
                        setShowFilterClienteDropdown(false);
                      }}
                      className={`px-3 py-2 cursor-pointer hover:bg-duetto-primary hover:bg-opacity-10 ${
                        filterClienteId === cliente.id ? 'bg-duetto-primary bg-opacity-10' : ''
                      }`}
                    >
                      <div className="font-medium text-gray-900 text-sm">{cliente.nombre_empresa}</div>
                      {cliente.persona_contacto && (
                        <div className="text-xs text-gray-500">{cliente.persona_contacto}</div>
                      )}
                    </div>
                  ))}
                {clientes.filter(cliente => 
                  cliente.nombre_empresa && 
                  cliente.nombre_empresa.toLowerCase().includes(filterCliente.toLowerCase())
                ).length === 0 && (
                  <div className="px-3 py-2 text-sm text-gray-500">
                    No se encontraron clientes
                  </div>
                )}
              </div>
            )}
            {filterClienteId && (
              <button
                onClick={() => {
                  setFilterCliente('');
                  setFilterClienteId('');
                }}
                className="absolute right-2 top-8 text-gray-400 hover:text-gray-600"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <div className="numero-dropdown-filter relative">
            <label className="block text-sm font-medium text-gray-700 mb-1">N° Presupuesto:</label>
            <input
              type="text"
              placeholder="Buscar número..."
              value={filterNumero}
              onChange={(e) => {
                const val = e.target.value.replace(/[^0-9]/g, ''); // Solo números
                setFilterNumero(val);
                setShowFilterNumeroDropdown(true);
                if (!val) {
                  setFilterNumeroSeleccionado(null);
                }
              }}
              onFocus={() => filterNumero && setShowFilterNumeroDropdown(true)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-duetto-primary"
            />
            {showFilterNumeroDropdown && filterNumero && (
              <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                {searchingNumero ? (
                  <div className="px-3 py-2 text-sm text-gray-500">Buscando...</div>
                ) : searchNumeroResults.length > 0 ? (
                  searchNumeroResults.map(p => (
                    <div
                      key={p.id}
                      data-uso="presupuestos.filtro"
                      data-uso-detalle="numero"
                      onClick={() => {
                        setFilterNumeroSeleccionado(p.numero);
                        setFilterNumero(String(p.numero));
                        setShowFilterNumeroDropdown(false);
                      }}
                      className={`px-3 py-2 cursor-pointer hover:bg-duetto-primary hover:bg-opacity-10 ${
                        filterNumeroSeleccionado === p.numero ? 'bg-duetto-primary bg-opacity-10' : ''
                      }`}
                    >
                      <div className="flex justify-between items-center">
                        <span className="font-bold text-duetto-primary">#{p.numero}</span>
                        <span className={`text-xs px-2 py-0.5 rounded ${
                          p.estado === 'confirmado' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                        }`}>{p.estado}</span>
                      </div>
                      <div className="text-xs text-gray-500 truncate">{p.cliente_nombre}</div>
                    </div>
                  ))
                ) : (
                  <div className="px-3 py-2 text-sm text-gray-500">
                    No se encontraron presupuestos
                  </div>
                )}
              </div>
            )}
            {filterNumeroSeleccionado && (
              <button
                onClick={() => {
                  setFilterNumero('');
                  setFilterNumeroSeleccionado(null);
                }}
                className="absolute right-2 top-8 text-gray-400 hover:text-gray-600"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Filtros:</label>
            <select
              value={filterEstado}
              onChange={(e) => setFilterEstado(e.target.value)}
              data-uso="presupuestos.filtro"
              data-uso-detalle="estado"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-duetto-primary"
            >
              <option value="">Todos</option>
              {ESTADOS_FILTRO.map(e => (
                <option key={e.valor} value={e.valor}>{e.texto}</option>
              ))}
            </select>
          </div>
          <div className="relative factura-dropdown-filter">
            <label className="block text-sm font-medium text-gray-700 mb-1">Factura:</label>
            <div className="relative">
              <input
                type="text"
                placeholder="Buscar por nº factura..."
                value={filterFactura}
                onChange={(e) => {
                  setFilterFactura(e.target.value);
                  setShowFilterFacturaDropdown(true);
                  if (!e.target.value) {
                    setFilterFacturaSeleccionada(null);
                  }
                }}
                onFocus={() => setShowFilterFacturaDropdown(true)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-duetto-primary"
              />
              {filterFacturaSeleccionada && (
                <button
                  onClick={() => {
                    setFilterFactura('');
                    setFilterFacturaSeleccionada(null);
                  }}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            {showFilterFacturaDropdown && filterFactura && searchFacturaResults.length > 0 && (
              <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                {searchingFactura ? (
                  <div className="p-3 text-sm text-gray-500">Buscando...</div>
                ) : (
                  searchFacturaResults.map((p) => {
                    const numFacturaSimple = facturaQueCoincide(p, filterFactura)
                      || extraerNumeroFactura(numerosFacturaDe(p)[0] || '');
                    return (
                      <button
                        key={p.id}
                        data-uso="presupuestos.filtro"
                        data-uso-detalle="factura"
                        onClick={() => {
                          setFilterFactura(numFacturaSimple);
                          setFilterFacturaSeleccionada(numFacturaSimple);
                          setShowFilterFacturaDropdown(false);
                        }}
                        className="w-full text-left px-3 py-2 hover:bg-gray-100 border-b border-gray-100 last:border-b-0"
                      >
                        <div className="flex justify-between items-center">
                          <span className="font-medium text-blue-600">Factura #{numFacturaSimple}</span>
                          <span className="text-xs text-gray-500">Presu #{p.numero}</span>
                        </div>
                        <div className="text-sm text-gray-600">{p.cliente_nombre}</div>
                      </button>
                    );
                  })
                )}
              </div>
            )}
            {showFilterFacturaDropdown && filterFactura && !searchingFactura && searchFacturaResults.length === 0 && (
              <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg p-3 text-sm text-gray-500">
                No se encontró factura con ese número
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-x-auto">
        <table className="w-full divide-y divide-gray-200" style={{ minWidth: '1200px' }}>
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ minWidth: '180px' }}>Nombre</th>
              <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ minWidth: '100px' }}>N° presu</th>
              <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ minWidth: '110px' }}>Factura</th>
              <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ minWidth: '110px' }}>Fecha presu</th>
              <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ minWidth: '110px' }}>Fecha evento</th>
              <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ minWidth: '90px' }}>Pendientes</th>
              <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ minWidth: '100px' }}>Importe</th>
              <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ minWidth: '80px' }}>Pagado</th>
              <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ minWidth: '110px' }}>Confirmado</th>
              <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ minWidth: '100px' }}>Beneficio</th>
              <th className="px-2 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ minWidth: '90px' }}>Acciones</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredPresupuestos.map((presupuesto) => {
              const beneficio = calculateBeneficio(presupuesto);
              return (
                <tr key={presupuesto.id} className="hover:bg-gray-50 cursor-pointer" data-uso="presupuestos.abrir" onClick={() => window.open(`/presupuestos/${presupuesto.id}`, '_blank')}>
                  <td className="px-3 py-4">
                    <div className="text-sm font-medium text-gray-900">{getClienteName(presupuesto.cliente_id)}</div>
                    <div className="text-sm text-gray-500 truncate" style={{ maxWidth: '160px' }}>{presupuesto.lugar_evento}</div>
                  </td>
                  <td className="px-2 py-4 whitespace-nowrap text-sm text-gray-900">
                    {getPresupuestoNumber(presupuesto)}
                  </td>
                  <td className="px-2 py-4 whitespace-nowrap text-sm">
                    {(() => {
                      // Multievento (M-39): todas las facturas de la cabecera y de las jornadas
                      const numeros = numerosFacturaDe(presupuesto);
                      if (numeros.length === 0) return <span className="text-gray-400 text-xs">Sin factura</span>;
                      if (numeros.length === 1) {
                        return (
                          <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs font-semibold">
                            #{extraerNumeroFactura(numeros[0])}
                          </span>
                        );
                      }
                      return (
                        <span
                          className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs font-semibold cursor-help"
                          title={numeros.map(n => `#${extraerNumeroFactura(n)}`).join(', ')}
                        >
                          {numeros.length} facturas
                        </span>
                      );
                    })()}
                  </td>
                  <td className="px-2 py-4 whitespace-nowrap text-sm text-gray-600">
                    {formatFecha(presupuesto.created_at)}
                  </td>
                  <td className="px-2 py-4 whitespace-nowrap text-sm text-gray-900">
                    {fechaEventoListado(presupuesto)}
                  </td>
                  {/* Indicadores de pendientes (U-70: criterios de Por hacer, en texto) */}
                  <td className="px-2 py-4 whitespace-nowrap text-center">
                    {presupuesto.estado === 'confirmado' && fechaFinEvento(presupuesto) && !esEventoPasado(presupuesto) && (() => {
                      const pendientes = pendientesEventoConfirmado(presupuesto);
                      if (pendientes.length === 0) {
                        return <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-emerald-100 text-emerald-700">Listo</span>;
                      }
                      return (
                        <div className="flex flex-wrap justify-center gap-1" style={{ maxWidth: '180px' }}>
                          {pendientes.map(pd => (
                            <span key={pd.clave} title={pd.detalle}
                              className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-amber-100 text-amber-800">
                              {pd.texto}
                            </span>
                          ))}
                        </div>
                      );
                    })()}
                    {presupuesto.estado !== 'confirmado' && (
                      <span className="text-gray-300 text-xs">-</span>
                    )}
                    {presupuesto.estado === 'confirmado' && esEventoPasado(presupuesto) && (
                      <span className="text-gray-300 text-xs">pasado</span>
                    )}
                  </td>
                  <td className="px-2 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    €{presupuesto.importe_total?.toFixed(2) || '0.00'}
                  </td>
                  <td className="px-2 py-4 whitespace-nowrap">
                    {/* U-69: valores antiguos (SI, NO, Parcial...) normalizados, sin tocar datos */}
                    {(() => {
                      const pago = normalizarEstadoPago(presupuesto.estado_pago);
                      return (
                        <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${pago.clase}`}>
                          {pago.texto}
                        </span>
                      );
                    })()}
                  </td>
                  <td className="px-2 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getEstadoBadgeColor(presupuesto.estado)}`}>
                      {presupuesto.estado === 'confirmado' ? 'sí' : 'no'}
                    </span>
                  </td>
                  <td className="px-2 py-4 whitespace-nowrap text-sm font-semibold">
                    <span className={beneficio >= 0 ? 'text-green-600' : 'text-red-600'}>
                      €{beneficio.toFixed(2)}
                    </span>
                  </td>
                  <td className="px-2 py-4 whitespace-nowrap text-right text-sm font-medium" onClick={(e) => e.stopPropagation()}>
                    <div className="flex justify-end space-x-2">
                      {sePuedeBorrar(presupuesto) && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(presupuesto.id);
                          }}
                          className="text-red-600 hover:text-red-900"
                          title="Eliminar"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {filteredPresupuestos.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            No se encontraron presupuestos
          </div>
        )}
        
        {/* Paginación */}
        {totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
            <div className="text-sm text-gray-700">
              Mostrando <span className="font-medium">{(currentPage - 1) * limit + 1}</span> a{' '}
              <span className="font-medium">{Math.min(currentPage * limit, total)}</span> de{' '}
              <span className="font-medium">{total}</span> presupuestos
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className={`px-4 py-2 rounded-lg font-medium ${
                  currentPage === 1
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                }`}
              >
                Anterior
              </button>
              
              {/* Números de página */}
              <div className="flex gap-1">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`px-4 py-2 rounded-lg font-medium ${
                        currentPage === pageNum
                          ? 'bg-duetto-primary text-white'
                          : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>
              
              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className={`px-4 py-2 rounded-lg font-medium ${
                  currentPage === totalPages
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                }`}
              >
                Siguiente
              </button>
            </div>
          </div>
        )}
      </div>
      </>)}

      {/* Alta en el móvil: pantalla completa y sus hojas */}
      {esMovil && showNewModal && renderAltaMovil()}
      {esMovil && renderHojasAltaMovil()}

      {/* Modal Nuevo Presupuesto Ampliado */}
      {showNewModal && !esMovil && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <h3 className="text-2xl font-bold text-gray-900">
                Nuevo Presupuesto {nextPresupuestoNumber && `#${nextPresupuestoNumber}`}
              </h3>
              <button onClick={handleCloseNewModal} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Tabs */}
            <div className="border-b border-gray-200">
              <nav className="flex">
                <button
                  onClick={() => setActiveTabNewPresupuesto('info')}
                  className={`flex-1 px-6 py-3 text-sm font-medium ${
                    activeTabNewPresupuesto === 'info'
                      ? 'border-b-2 border-duetto-primary text-duetto-primary bg-duetto-primary bg-opacity-5'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  1. Información Básica
                </button>
                <button
                  disabled
                  className="flex-1 px-6 py-3 text-sm font-medium text-gray-400 cursor-not-allowed opacity-50"
                  title="Los menús se añaden después de crear el presupuesto"
                >
                  2. Añadir Menús
                </button>
                <button
                  disabled
                  className="flex-1 px-6 py-3 text-sm font-medium text-gray-400 cursor-not-allowed opacity-50"
                  title="Los menús personalizados se crean después de crear el presupuesto"
                >
                  3. Menú Personalizado
                </button>
              </nav>
              <p className="text-xs text-gray-500 px-6 py-2 bg-amber-50 border-t border-amber-100">
                💡 Crea primero el presupuesto con la información básica. Luego podrás añadir menús y personalizar en la página de detalle.
              </p>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {/* Tab 1: Info Básica */}
              {activeTabNewPresupuesto === 'info' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Cliente <span className="text-red-500">*</span>
                    </label>
                    <div className="flex gap-2">
                      <div className="relative flex-1 cliente-dropdown-modal">
                        <input
                          type="text"
                          value={clienteSearchTerm}
                          onChange={(e) => {
                            setClienteSearchTerm(e.target.value);
                            setShowClienteDropdown(true);
                            if (!e.target.value) {
                              setNewPresupuesto({ ...newPresupuesto, cliente_id: '', persona_contacto_id: '', contacto_facturacion_id: '' });
                            }
                          }}
                          onFocus={() => setShowClienteDropdown(true)}
                          placeholder="Buscar cliente por nombre..."
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-duetto-primary"
                        />
                        {showClienteDropdown && (
                        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto cliente-dropdown-list">
                          {clientes
                            .filter(cliente => 
                              cliente.nombre_empresa && cliente.nombre_empresa.toLowerCase().includes(clienteSearchTerm.toLowerCase())
                            )
                            .sort((a, b) => (a.nombre_empresa || '').localeCompare(b.nombre_empresa || ''))
                            .map(cliente => (
                              <div
                                key={cliente.id}
                                data-cliente-id={cliente.id}
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setNewPresupuesto({ ...newPresupuesto, cliente_id: cliente.id, persona_contacto_id: '', contacto_facturacion_id: '' });
                                  setClienteSearchTerm(cliente.nombre_empresa);
                                  setShowClienteDropdown(false);
                                }}
                                className={`cliente-dropdown-item px-3 py-2 cursor-pointer hover:bg-duetto-primary hover:bg-opacity-10 ${
                                  newPresupuesto.cliente_id === cliente.id ? 'bg-duetto-primary bg-opacity-5' : ''
                                }`}
                              >
                                <div className="font-medium text-gray-900">{cliente.nombre_empresa}</div>
                                {cliente.contacto_principal_nombre && (
                                  <div className="text-xs text-gray-500">
                                    Contacto: {cliente.contacto_principal_nombre}
                                  </div>
                                )}
                              </div>
                            ))}
                          {clientes.filter(cliente => 
                            cliente.nombre_empresa && cliente.nombre_empresa.toLowerCase().includes(clienteSearchTerm.toLowerCase())
                          ).length === 0 && (
                            <div className="px-3 py-2 text-sm text-gray-500">
                              No se encontraron clientes
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowAddClienteModal(true)}
                      className="px-4 py-2 bg-duetto-secondary text-white rounded-lg hover:bg-opacity-90 whitespace-nowrap"
                    >
                      + Nuevo Cliente
                    </button>
                  </div>
                </div>

                  {/* Contactos del presupuesto */}
                  {newPresupuesto.cliente_id && (() => {
                    const clienteSel = clientes.find(c => c.id === newPresupuesto.cliente_id);
                    const tienePrincipal = !!(clienteSel && (clienteSel.persona_contacto || clienteSel.email || clienteSel.telefono));
                    const sinContactos = !tienePrincipal && personasContactoCliente.length === 0;
                    const nombrePrincipal = clienteSel ? (clienteSel.persona_contacto || clienteSel.nombre_empresa || 'Contacto principal') : '';
                    const opcionesContacto = [...personasContactoCliente].sort((a, b) => (a.nombre || '').localeCompare(b.nombre || ''));
                    return (
                      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 space-y-3">
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-semibold text-gray-800">👤 Contactos del presupuesto</p>
                          <button
                            type="button"
                            onClick={() => {
                              setErrorNuevoContacto(null);
                              setNuevoContacto({ nombre: '', cargo: '', email: '', telefono: '', extension: '', notas: '' });
                              setShowNuevoContacto(true);
                            }}
                            className="text-xs text-duetto-primary hover:bg-emerald-100 px-2 py-1 rounded font-medium inline-flex items-center gap-1"
                          >
                            + Nuevo contacto
                          </button>
                        </div>
                        {sinContactos ? (
                          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                            ⚠️ Este cliente no tiene contactos ni email/teléfono guardados. Pulsa <strong>"+ Nuevo contacto"</strong> arriba para añadir uno sin salir de aquí.
                          </p>
                        ) : (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">
                                Contacto del evento <span className="text-red-500">*</span>
                              </label>
                              <select
                                value={newPresupuesto.persona_contacto_id}
                                onChange={(e) => setNewPresupuesto({ ...newPresupuesto, persona_contacto_id: e.target.value })}
                                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-duetto-primary"
                              >
                                <option value="">-- Selecciona contacto --</option>
                                {tienePrincipal && (
                                  <option value="principal">{nombrePrincipal} (contacto principal)</option>
                                )}
                                {opcionesContacto.map(p => (
                                  <option key={p.id} value={p.id}>{p.nombre}{p.cargo ? ` - ${p.cargo}` : ''}</option>
                                ))}
                              </select>
                              <p className="text-xs text-gray-500 mt-1">Persona presente el día del evento.</p>
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">
                                Contacto de facturación
                              </label>
                              <select
                                value={newPresupuesto.contacto_facturacion_id}
                                onChange={(e) => setNewPresupuesto({ ...newPresupuesto, contacto_facturacion_id: e.target.value })}
                                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-duetto-primary"
                              >
                                <option value="">-- Mismo que el del evento --</option>
                                {tienePrincipal && (
                                  <option value="principal">{nombrePrincipal} (contacto principal)</option>
                                )}
                                {opcionesContacto.map(p => (
                                  <option key={p.id} value={p.id}>{p.nombre}{p.cargo ? ` - ${p.cargo}` : ''}</option>
                                ))}
                              </select>
                              <p className="text-xs text-gray-500 mt-1">A quién se le envía la factura.</p>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* Toggle Multi-Evento */}
                  <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg p-4 border border-indigo-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                          <Calendar className="w-5 h-5 text-indigo-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">Presupuesto Multi-Evento</p>
                          <p className="text-sm text-gray-500">Para eventos que se repiten en varios días</p>
                        </div>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={esMultiEvento}
                          onChange={(e) => setEsMultiEvento(e.target.checked)}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                      </label>
                    </div>
                  </div>

                  {/* Configuración Multi-Evento */}
                  {esMultiEvento && (
                    <div className="bg-white border border-indigo-200 rounded-lg p-4 space-y-4">
                      <h4 className="font-semibold text-gray-900 flex items-center">
                        <Calendar className="w-4 h-4 mr-2 text-indigo-600" />
                        Configurar Jornadas
                      </h4>
                      
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Fecha Inicio <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="date"
                            value={multiEventoConfig.fecha_inicio}
                            onChange={(e) => setMultiEventoConfig({...multiEventoConfig, fecha_inicio: e.target.value})}
                            min={fechaLocalISO()}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Fecha Fin <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="date"
                            value={multiEventoConfig.fecha_fin}
                            onChange={(e) => setMultiEventoConfig({...multiEventoConfig, fecha_fin: e.target.value})}
                            min={multiEventoConfig.fecha_inicio || fechaLocalISO()}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          />
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-6">
                        <label className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={multiEventoConfig.excluir_fines_semana}
                            onChange={(e) => setMultiEventoConfig({...multiEventoConfig, excluir_fines_semana: e.target.checked})}
                            className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                          />
                          <span className="text-sm text-gray-700">Excluir fines de semana</span>
                        </label>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Total Personas a Distribuir
                          </label>
                          <input
                            type="number"
                            value={multiEventoConfig.num_personas_total}
                            onChange={(e) => setMultiEventoConfig({...multiEventoConfig, num_personas_total: parseInt(e.target.value) || 0})}
                            min="1"
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Distribución
                          </label>
                          <select
                            value={multiEventoConfig.distribucion}
                            onChange={(e) => setMultiEventoConfig({...multiEventoConfig, distribucion: e.target.value})}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          >
                            <option value="igual">Igual entre días</option>
                            <option value="personalizado">Personalizar después</option>
                          </select>
                        </div>
                      </div>
                      
                      {/* Preview de Jornadas con edición de personas */}
                      {jornadasPreview.length > 0 && (
                        <div className="bg-gray-50 rounded-lg p-3">
                          <p className="text-sm font-medium text-gray-700 mb-2">
                            Vista previa: {jornadasPreview.length} jornadas
                          </p>
                          
                          {/* Tabla editable de jornadas */}
                          <div className="max-h-64 overflow-y-auto">
                            <table className="w-full text-sm">
                              <thead className="bg-gray-100 sticky top-0">
                                <tr>
                                  <th className="px-2 py-1 text-left text-xs font-medium text-gray-600">Día</th>
                                  <th className="px-2 py-1 text-left text-xs font-medium text-gray-600">Fecha</th>
                                  <th className="px-2 py-1 text-center text-xs font-medium text-gray-600">Personas</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-200">
                                {jornadasPreview.map((jp, idx) => {
                                  const fecha = new Date(jp.fecha);
                                  const dias = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
                                  return (
                                    <tr key={jp.fecha || idx} className="bg-white">
                                      <td className="px-2 py-1 font-medium text-gray-900">
                                        {dias[fecha.getUTCDay()]}
                                      </td>
                                      <td className="px-2 py-1 text-gray-600">
                                        {fecha.getUTCDate()}/{fecha.getUTCMonth()+1}/{fecha.getUTCFullYear()}
                                      </td>
                                      <td className="px-2 py-1 text-center">
                                        <input
                                          type="number"
                                          min="0"
                                          value={jp.num_personas}
                                          onChange={(e) => {
                                            const newJornadas = [...jornadasPreview];
                                            newJornadas[idx].num_personas = parseInt(e.target.value) || 0;
                                            setJornadasPreview(newJornadas);
                                          }}
                                          className="w-20 px-2 py-1 text-center border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                        />
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                          
                          {/* Resumen y validación */}
                          <div className="mt-3 pt-2 border-t border-gray-200">
                            <div className="flex justify-between items-center">
                              <span className="text-sm text-gray-600">Total asignado:</span>
                              <span className={`font-semibold ${
                                jornadasPreview.reduce((sum, j) => sum + j.num_personas, 0) === multiEventoConfig.num_personas_total
                                  ? 'text-green-600'
                                  : jornadasPreview.reduce((sum, j) => sum + j.num_personas, 0) > multiEventoConfig.num_personas_total
                                    ? 'text-red-600'
                                    : 'text-orange-600'
                              }`}>
                                {jornadasPreview.reduce((sum, j) => sum + j.num_personas, 0)} / {multiEventoConfig.num_personas_total} personas
                              </span>
                            </div>
                            {jornadasPreview.reduce((sum, j) => sum + j.num_personas, 0) !== multiEventoConfig.num_personas_total && (
                              <p className={`text-xs mt-1 ${
                                jornadasPreview.reduce((sum, j) => sum + j.num_personas, 0) > multiEventoConfig.num_personas_total
                                  ? 'text-red-500'
                                  : 'text-orange-500'
                              }`}>
                                {jornadasPreview.reduce((sum, j) => sum + j.num_personas, 0) > multiEventoConfig.num_personas_total
                                  ? `⚠️ Excedes ${jornadasPreview.reduce((sum, j) => sum + j.num_personas, 0) - multiEventoConfig.num_personas_total} personas del total contratado`
                                  : `⚠️ Faltan ${multiEventoConfig.num_personas_total - jornadasPreview.reduce((sum, j) => sum + j.num_personas, 0)} personas por asignar`
                                }
                              </p>
                            )}
                            {jornadasPreview.reduce((sum, j) => sum + j.num_personas, 0) === multiEventoConfig.num_personas_total && (
                              <p className="text-xs mt-1 text-green-600">
                                ✅ Distribución correcta
                              </p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Campos de fecha/lugar solo si NO es multi-evento */}
                  {!esMultiEvento && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Fecha del Evento <span className="text-red-500">*</span>
                      </label>
                      <DatePicker
                        value={newPresupuesto.fecha_evento}
                        onChange={(date) => setNewPresupuesto({ ...newPresupuesto, fecha_evento: date })}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Número de Personas <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        min="1"
                        value={newPresupuesto.num_personas || ''}
                        onChange={(e) => cambiarPersonasAlta(e.target.value === '' ? '' : (parseInt(e.target.value) || 1))}
                        onBlur={(e) => { if (e.target.value === '') cambiarPersonasAlta(1); }}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2"
                      />
                    </div>
                  </div>
                  )}

                  {/* El menú se elige en la pestaña Menús (el buscador de la pestaña de datos se quitó el 20-oct-2026 a petición de Alfonso) */}

                  {/* Lugar del evento (común para ambos tipos) */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Lugar del Evento {!esMultiEvento && <span className="text-red-500">*</span>}
                    </label>
                    <DireccionAutocomplete
                      id="address-autocomplete-modal"
                      value={newPresupuesto.lugar_evento}
                      // Al escribir a mano se olvidan las coordenadas de una sugerencia anterior
                      // (al elegir sugerencia, onSelect las vuelve a poner justo después)
                      onChange={(lugar) => setNewPresupuesto(prev => ({ ...prev, lugar_evento: lugar, latitud: null, longitud: null }))}
                      onSelect={(lugar) => setNewPresupuesto(prev => ({
                        ...prev,
                        lugar_evento: lugar.direccion || lugar.nombre,
                        latitud: lugar.lat,
                        longitud: lugar.lng,
                      }))}
                      placeholder="Buscar dirección con Google Maps..."
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    />
                    {esMultiEvento && (
                      <p className="text-xs text-gray-500 mt-1">Este lugar se aplicará a todas las jornadas (puedes cambiarlo después)</p>
                    )}
                  </div>

                  {/* Horarios (comunes para ambos tipos) */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Hora Inicio</label>
                      <input
                        type="time"
                        value={newPresupuesto.hora_inicio}
                        onChange={(e) => setNewPresupuesto({ ...newPresupuesto, hora_inicio: e.target.value })}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Hora Fin</label>
                      <input
                        type="time"
                        value={newPresupuesto.hora_fin}
                        onChange={(e) => setNewPresupuesto({ ...newPresupuesto, hora_fin: e.target.value })}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Observaciones</label>
                    <textarea
                      value={newPresupuesto.observaciones}
                      onChange={(e) => setNewPresupuesto({ ...newPresupuesto, observaciones: e.target.value })}
                      placeholder="Notas adicionales..."
                      rows="3"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 resize-none"
                    />
                  </div>
                </div>
              )}

              {/* Tab 2: Añadir Menús Existentes */}
              {activeTabNewPresupuesto === 'menus' && (
                <div className="space-y-4">
                  <h4 className="font-semibold text-gray-900">Menús Disponibles</h4>
                  
                  {/* Buscador de menús */}
                  <div className="mb-4">
                    <input
                      type="text"
                      value={menuSearchTerm}
                      onChange={(e) => setMenuSearchTerm(e.target.value)}
                      placeholder="Buscar menú por nombre..."
                      className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-duetto-primary"
                    />
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {menus
                      .filter(menu => 
                        menu.nombre && menu.nombre.toLowerCase().includes(menuSearchTerm.toLowerCase())
                      )
                      .sort((a, b) => (a.nombre || '').localeCompare(b.nombre || ''))
                      .map(menu => {
                      const isSelected = selectedMenus.some(sm => sm.menu_id === menu.id);
                      const selectedMenu = selectedMenus.find(sm => sm.menu_id === menu.id);
                      
                      return (
                        <div
                          key={menu.id}
                          className={`border rounded-lg p-4 cursor-pointer transition-all ${
                            isSelected ? 'border-duetto-primary bg-duetto-primary bg-opacity-5' : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <div className="flex justify-between items-start mb-2">
                            <div className="flex-1">
                              <h5 className="font-medium text-gray-900">{menu.nombre}</h5>
                              <p className="text-sm text-gray-600">€{(precioMenuCatalogo(menu) || 0).toFixed(2)} / persona</p>
                            </div>
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedMenus([...selectedMenus, { menu_id: menu.id, num_personas: newPresupuesto.num_personas }]);
                                } else {
                                  setSelectedMenus(selectedMenus.filter(sm => sm.menu_id !== menu.id));
                                }
                              }}
                              className="text-duetto-primary w-5 h-5"
                            />
                          </div>
                          {isSelected && (
                            <div className="mt-2">
                              <label className="block text-xs font-medium text-gray-700 mb-1">Personas para este menú:</label>
                              <input
                                type="number"
                                min="1"
                                value={selectedMenu?.num_personas || newPresupuesto.num_personas || ''}
                                onChange={(e) => {
                                  const updated = selectedMenus.map(sm =>
                                    sm.menu_id === menu.id ? { ...sm, num_personas: e.target.value === '' ? '' : (parseInt(e.target.value) || 1) } : sm
                                  );
                                  setSelectedMenus(updated);
                                }}
                                onBlur={(e) => {
                                  if (e.target.value === '') {
                                    const updated = selectedMenus.map(sm =>
                                      sm.menu_id === menu.id ? { ...sm, num_personas: 1 } : sm
                                    );
                                    setSelectedMenus(updated);
                                  }
                                }}
                                className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                              />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  
                  {/* Menús Seleccionados y Orden */}
                  {selectedMenus.length > 0 && (
                    <div className="mt-6 border-t pt-6">
                      <h4 className="font-semibold text-gray-900 mb-3">Orden de Menús Seleccionados</h4>
                      <div className="space-y-2">
                        {selectedMenus.map((sm, index) => {
                          const menu = menus.find(m => m.id === sm.menu_id);
                          if (!menu) return null;
                          
                          return (
                            <div key={sm.menu_id || index} className="flex items-center gap-3 bg-white border border-gray-200 rounded-lg p-3">
                              <div className="flex flex-col gap-1">
                                <button
                                  onClick={() => moveMenuUp(index)}
                                  disabled={index === 0}
                                  className={`p-1 rounded ${
                                    index === 0 
                                      ? 'text-gray-300 cursor-not-allowed' 
                                      : 'text-gray-600 hover:bg-gray-100'
                                  }`}
                                  title="Subir"
                                >
                                  <ChevronUp className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => moveMenuDown(index)}
                                  disabled={index === selectedMenus.length - 1}
                                  className={`p-1 rounded ${
                                    index === selectedMenus.length - 1 
                                      ? 'text-gray-300 cursor-not-allowed' 
                                      : 'text-gray-600 hover:bg-gray-100'
                                  }`}
                                  title="Bajar"
                                >
                                  <ChevronDown className="w-4 h-4" />
                                </button>
                              </div>
                              <div className="flex items-center justify-center w-8 h-8 bg-duetto-primary text-white rounded-full text-sm font-semibold">
                                {index + 1}
                              </div>
                              <div className="flex-1">
                                <div className="font-medium text-gray-900">{menu.nombre}</div>
                                <div className="text-sm text-gray-600">{sm.num_personas} personas</div>
                              </div>
                              <button
                                onClick={() => setSelectedMenus(selectedMenus.filter((_, i) => i !== index))}
                                className="p-2 text-red-600 hover:bg-red-50 rounded"
                                title="Eliminar"
                              >
                                <X className="w-5 h-5" />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Tab 3: Menú Personalizado */}
              {activeTabNewPresupuesto === 'personalizado' && (
                <div className="space-y-4">
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Nombre del Menú</label>
                      <input
                        type="text"
                        value={customMenuName}
                        onChange={(e) => setCustomMenuName(e.target.value)}
                        placeholder="Ej: Menú Personalizado Empresa / 01/01/2025"
                        className="w-full border border-gray-300 rounded-lg px-3 py-2"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Personas</label>
                      <input
                        type="number"
                        value={customMenuPersonas}
                        onChange={(e) => setCustomMenuPersonas(e.target.value)}
                        placeholder={`Por defecto: ${newPresupuesto.num_personas}`}
                        min="1"
                        className="w-full border border-gray-300 rounded-lg px-3 py-2"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Precio/Persona (€) <span className="text-gray-400 text-xs">(opcional)</span>
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={customMenuPrecioNew}
                        onChange={(e) => setCustomMenuPrecioNew(e.target.value)}
                        placeholder="0.00 - Dejar vacío para menú placeholder"
                        min="0"
                        className="w-full border border-gray-300 rounded-lg px-3 py-2"
                      />
                      <p className="text-xs font-medium text-orange-600 mt-1">
                        💰 Coste total de ingredientes: €{calculateCustomMenuPriceNew().toFixed(2)} - Añade tu margen para definir el precio de venta
                      </p>
                    </div>
                  </div>

                  {/* Filtros */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 mb-3">Filtros</h4>
                    
                    {/* Buscador de platos */}
                    <div className="mb-4">
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        <Search className="inline w-3 h-3 mr-1" />
                        Buscar plato por nombre
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          value={platoFiltersNew.searchText}
                          onChange={(e) => setPlatoFiltersNew({...platoFiltersNew, searchText: e.target.value})}
                          placeholder="Ej: Bollería, Salmorejo, Hummus..."
                          className="w-full px-3 py-2 pr-8 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-duetto-primary focus:border-transparent"
                          autoFocus
                        />
                        {platoFiltersNew.searchText && (
                          <button
                            onClick={() => setPlatoFiltersNew({...platoFiltersNew, searchText: ''})}
                            className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                      {platoFiltersNew.searchText && getFilteredPlatosNew().length === 0 && (
                        <p className="mt-2 text-xs text-red-600">
                          No se encontraron platos con &ldquo;{platoFiltersNew.searchText}&rdquo;
                        </p>
                      )}
                    </div>
                    
                    <div className="grid grid-cols-4 gap-4 mb-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Precio máx</label>
                        <input
                          type="number"
                          value={platoFiltersNew.precioMax}
                          onChange={(e) => setPlatoFiltersNew({...platoFiltersNew, precioMax: e.target.value})}
                          placeholder="€"
                          className="w-full px-2 py-1 text-sm border rounded"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Tiempo máx</label>
                        <input
                          type="number"
                          value={platoFiltersNew.tiempoMax}
                          onChange={(e) => setPlatoFiltersNew({...platoFiltersNew, tiempoMax: e.target.value})}
                          placeholder="min"
                          className="w-full px-2 py-1 text-sm border rounded"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Categoría</label>
                        <select
                          value={platoFiltersNew.categoria}
                          onChange={(e) => setPlatoFiltersNew({...platoFiltersNew, categoria: e.target.value})}
                          className="w-full px-2 py-1 text-sm border rounded"
                        >
                          <option value="">Todas</option>
                          <option value="desayuno">Desayuno</option>
                          <option value="comida">Comida</option>
                          <option value="postre">Postre</option>
                          <option value="bebida">Bebida</option>
                        </select>
                      </div>
                    </div>

                    {/* Características alimentarias */}
                    <div className="mb-3">
                      <label className="block text-xs font-medium text-gray-700 mb-2">Evitar características:</label>
                      <div className="flex flex-wrap gap-3">
                        <label className="flex items-center text-xs">
                          <input
                            type="checkbox"
                            checked={platoFiltersNew.contiene_carne}
                            onChange={(e) => setPlatoFiltersNew({...platoFiltersNew, contiene_carne: e.target.checked})}
                            className="mr-1"
                          />
                          Contiene carne
                        </label>
                        <label className="flex items-center text-xs">
                          <input
                            type="checkbox"
                            checked={platoFiltersNew.derivado_animal}
                            onChange={(e) => setPlatoFiltersNew({...platoFiltersNew, derivado_animal: e.target.checked})}
                            className="mr-1"
                          />
                          Derivado animal
                        </label>
                        <label className="flex items-center text-xs">
                          <input
                            type="checkbox"
                            checked={platoFiltersNew.pasteurizado}
                            onChange={(e) => setPlatoFiltersNew({...platoFiltersNew, pasteurizado: e.target.checked})}
                            className="mr-1"
                          />
                          Pasteurizado
                        </label>
                      </div>
                    </div>

                    {/* Alérgenos - TODOS */}
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-2">Evitar alérgenos (14 legales):</label>
                      <div className="grid grid-cols-4 gap-2">
                        {alergenos.map(alergeno => (
                          <label key={alergeno.id} className="flex items-center text-xs">
                            <input
                              type="checkbox"
                              checked={platoFiltersNew.alergenosEvitar.includes(alergeno.id)}
                              onChange={(e) => {
                                const updated = e.target.checked
                                  ? [...platoFiltersNew.alergenosEvitar, alergeno.id]
                                  : platoFiltersNew.alergenosEvitar.filter(id => id !== alergeno.id);
                                setPlatoFiltersNew({...platoFiltersNew, alergenosEvitar: updated});
                              }}
                              className="mr-1"
                            />
                            <AlergenoIcon nombre={alergeno.nombre} size="sm" />
                            <span className="ml-1">{alergeno.nombre}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Lista de platos */}
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">
                      Platos Disponibles ({getFilteredPlatosNew().length})
                      <span className="ml-2 text-sm font-medium text-orange-600">
                        {selectedPlatosCustom.length} seleccionados - Coste total: €{calculateCustomMenuPriceNew().toFixed(2)}
                      </span>
                    </h4>
                    <div className="grid grid-cols-2 gap-3 max-h-80 overflow-y-auto">
                      {getFilteredPlatosNew().map(plato => {
                        const platoSelected = selectedPlatosCustom.find(p => p.platoId === plato.id);
                        const isSelected = !!platoSelected;
                        
                        return (
                          <div
                            key={plato.id}
                            className={`border rounded-lg p-3 transition-all ${
                              isSelected
                                ? 'border-duetto-primary bg-duetto-primary bg-opacity-5'
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            <div className="flex items-center space-x-2">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => {
                                  setSelectedPlatosCustom(prev => {
                                    const exists = prev.find(p => p.platoId === plato.id);
                                    if (exists) {
                                      return prev.filter(p => p.platoId !== plato.id);
                                    } else {
                                      return [...prev, { platoId: plato.id, cantidad: 1 }];
                                    }
                                  });
                                }}
                                className="text-duetto-primary"
                              />
                              <div className="flex-1">
                                <h5 className="font-medium text-sm text-gray-900">{plato.nombre}</h5>
                                <div className="flex items-center space-x-2 text-xs text-gray-600">
                                  <span className="font-medium text-orange-600">Coste: €{(plato.coste || 0).toFixed(2)}/ud</span>
                                  <span>•</span>
                                  <span>{plato.tiempo_preparacion_min} min</span>
                                  {plato.categoria && (
                                    <>
                                      <span>•</span>
                                      <span className="capitalize">{plato.categoria}</span>
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>
                            
                            {/* Input de cantidad */}
                            {isSelected && (
                              <div className="mt-2 pt-2 border-t border-gray-200">
                                <div className="flex items-center justify-between">
                                  <label className="text-xs font-medium text-gray-700">Cantidad:</label>
                                  <div className="flex items-center space-x-2">
                                    <input
                                      type="number"
                                      min="1"
                                      value={platoSelected.cantidad}
                                      onChange={(e) => {
                                        e.stopPropagation();
                                        const cantidad = e.target.value === '' ? '' : (parseInt(e.target.value) || 1);
                                        setSelectedPlatosCustom(prev => 
                                          prev.map(p => p.platoId === plato.id ? { ...p, cantidad } : p)
                                        );
                                      }}
                                      onBlur={(e) => {
                                        // Si está vacío al perder foco, poner 1
                                        if (e.target.value === '' || parseInt(e.target.value) < 1) {
                                          setSelectedPlatosCustom(prev => 
                                            prev.map(p => p.platoId === plato.id ? { ...p, cantidad: 1 } : p)
                                          );
                                        }
                                      }}
                                      onClick={(e) => e.stopPropagation()}
                                      className="w-14 px-2 py-1 text-xs border rounded"
                                    />
                                    <span className="text-xs font-medium text-orange-600">
                                      Coste total: €{((plato.coste || 0) * (platoSelected.cantidad || 0)).toFixed(2)}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Footer con resumen */}
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
              <div className="flex justify-between items-start mb-4">
                <div className="text-sm flex-1">
                  <div className="font-semibold text-gray-900 mb-2">Resumen de Menús:</div>
                  {selectedMenus.length === 0 && selectedPlatosCustom.length === 0 ? (
                    <div className="text-gray-500 italic">No hay menús seleccionados</div>
                  ) : (
                    <div className="space-y-1">
                      {selectedMenus.map((sm, index) => {
                        const menu = menus.find(m => m.id === sm.menu_id);
                        return menu ? (
                          <div key={sm.menu_id || index} className="text-gray-700 flex items-center">
                            <span className="w-2 h-2 bg-duetto-primary rounded-full mr-2"></span>
                            <span className="font-medium">{menu.nombre}</span>
                            <span className="text-gray-500 ml-2">({sm.num_personas} personas)</span>
                          </div>
                        ) : null;
                      })}
                      {selectedPlatosCustom.length > 0 && (
                        <div className="text-gray-700 flex items-center">
                          <span className="w-2 h-2 bg-duetto-secondary rounded-full mr-2"></span>
                          <span className="font-medium">Menú Personalizado</span>
                          <span className="text-gray-500 ml-2">({customMenuPersonas || newPresupuesto.num_personas} personas)</span>
                        </div>
                      )}
                      <div className="text-gray-600 text-xs mt-2 pt-2 border-t border-gray-300">
                        Total: {selectedMenus.length + (selectedPlatosCustom.length > 0 ? 1 : 0)} menú(s)
                      </div>
                    </div>
                  )}
                </div>
                <div className="text-right ml-6">
                  <div className="text-sm text-gray-600">Total estimado:</div>
                  <div className="text-2xl font-bold text-duetto-primary">
                    €{calculateTotalsForNewPresupuesto().total.toFixed(2)}
                  </div>
                </div>
              </div>
              <div className="flex space-x-3">
                <button
                  onClick={() => {
                    setShowNewModal(false);
                    setActiveTabNewPresupuesto('info');
                  }}
                  className="flex-1 border border-gray-300 text-gray-700 py-2 rounded-lg hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleCreatePresupuesto}
                  className="flex-1 bg-duetto-primary text-white py-2 rounded-lg hover:bg-opacity-90"
                >
                  Crear Presupuesto
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Mini-Modal para Añadir Contacto Rápido (inline, sin cerrar Nuevo Presupuesto) */}
      {showNuevoContacto && !esMovil && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="flex justify-between items-center p-5 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">+ Nuevo contacto</h3>
              <button
                type="button"
                onClick={() => setShowNuevoContacto(false)}
                className="text-gray-400 hover:text-gray-700 text-xl leading-none"
              >×</button>
            </div>
            <div className="p-5 space-y-3">
              <p className="text-xs text-gray-500">
                Crearás un contacto nuevo para el cliente seleccionado. Al guardar, se asignará automáticamente como contacto del evento.
              </p>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Nombre <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={nuevoContacto.nombre}
                  onChange={(e) => setNuevoContacto({ ...nuevoContacto, nombre: e.target.value })}
                  placeholder="Ej: Manuel García"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-duetto-primary"
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Cargo</label>
                <input
                  type="text"
                  value={nuevoContacto.cargo}
                  onChange={(e) => setNuevoContacto({ ...nuevoContacto, cargo: e.target.value })}
                  placeholder="Ej: Director RRHH"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-duetto-primary"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    value={nuevoContacto.email}
                    onChange={(e) => setNuevoContacto({ ...nuevoContacto, email: e.target.value })}
                    placeholder="email@empresa.com"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-duetto-primary"
                  />
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div className="col-span-2">
                    <label className="block text-xs font-medium text-gray-700 mb-1">Teléfono</label>
                    <input
                      type="tel"
                      value={nuevoContacto.telefono}
                      onChange={(e) => setNuevoContacto({ ...nuevoContacto, telefono: e.target.value })}
                      placeholder="+34 600 000 000"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-duetto-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Extensión</label>
                    <input
                      type="text"
                      value={nuevoContacto.extension}
                      onChange={(e) => setNuevoContacto({ ...nuevoContacto, extension: e.target.value })}
                      placeholder="Ext."
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-duetto-primary"
                      data-testid="nuevo-contacto-extension"
                    />
                  </div>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Notas</label>
                <textarea
                  value={nuevoContacto.notas}
                  onChange={(e) => setNuevoContacto({ ...nuevoContacto, notas: e.target.value })}
                  placeholder="Horario, preferencias, cómo contactar..."
                  rows={2}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-duetto-primary"
                  data-testid="nuevo-contacto-notas"
                />
              </div>
              {errorNuevoContacto && (
                <div className="bg-red-50 border border-red-200 rounded px-3 py-2 text-xs text-red-800">
                  {errorNuevoContacto}
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 p-5 border-t border-gray-200 bg-gray-50">
              <button
                type="button"
                onClick={() => setShowNuevoContacto(false)}
                disabled={creandoContacto}
                className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleCrearNuevoContacto}
                disabled={creandoContacto || !nuevoContacto.nombre.trim()}
                className="px-4 py-2 text-sm bg-duetto-primary text-white rounded-lg hover:opacity-90 disabled:opacity-50"
              >
                {creandoContacto ? 'Guardando…' : 'Guardar y seleccionar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mini-Modal para Añadir Cliente Rápido */}
      {showAddClienteModal && !esMovil && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="flex justify-between items-center p-6 border-b border-gray-200">
              <h3 className="text-xl font-semibold text-gray-900">Nuevo Cliente Rápido</h3>
              <button onClick={() => setShowAddClienteModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre Empresa <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={newClienteQuick.nombre_empresa}
                  onChange={(e) => setNewClienteQuick({ ...newClienteQuick, nombre_empresa: e.target.value })}
                  placeholder="Nombre de la empresa"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Contacto Principal
                </label>
                <input
                  type="text"
                  value={newClienteQuick.contacto_principal_nombre}
                  onChange={(e) => setNewClienteQuick({ ...newClienteQuick, contacto_principal_nombre: e.target.value })}
                  placeholder="Nombre del contacto"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Teléfono
                </label>
                <input
                  type="tel"
                  value={newClienteQuick.telefono_principal}
                  onChange={(e) => setNewClienteQuick({ ...newClienteQuick, telefono_principal: e.target.value })}
                  placeholder="+34 600 000 000"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={newClienteQuick.email}
                  onChange={(e) => setNewClienteQuick({ ...newClienteQuick, email: e.target.value })}
                  placeholder="email@empresa.com"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                />
              </div>
            </div>
            
            <div className="flex gap-3 p-6 border-t border-gray-200">
              <button
                onClick={() => {
                  setShowAddClienteModal(false);
                  setNewClienteQuick({
                    nombre_empresa: '',
                    contacto_principal_nombre: '',
                    telefono_principal: '',
                    email: ''
                  });
                }}
                className="flex-1 bg-gray-200 text-gray-700 py-2 rounded-lg hover:bg-gray-300"
              >
                Cancelar
              </button>
              <button
                onClick={handleAddClienteQuick}
                className="flex-1 bg-duetto-primary text-white py-2 rounded-lg hover:bg-opacity-90"
              >
                Añadir Cliente
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Presupuestos;